import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "en" | "ru" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved === "ru" || saved === "es" ? saved : "en") as Language;
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    "nav.home": "Home",
    "nav.about": "About",
    "nav.services": "Services",
    "nav.ministries": "Ministries",
    "nav.contact": "Contact Us",
    "nav.donate": "Donate",
    "nav.resources": "Resources",
    "nav.blog": "Blog",
    "nav.specialevents": "Special Events",
    "nav.bibleschool": "Bible School",
    "nav.dailybiblereading": "Daily Bible Reading",
    "nav.admin": "Admin",
    "nav.signin": "Sign In",
    "nav.signout": "Sign Out",
    "nav.profile": "My Profile",
    "nav.contributor": "Become a Contributor",
    "nav.announcements": "Announcements",
    "nav.members": "Members",
    "nav.youtubevideos": "YouTube Videos",
    
    // YouTube Videos
    "youtube.latest.title": "Latest Video",
    "youtube.latest.subtitle": "Watch our most recent teaching",
    "youtube.latest.viewall": "View All Videos",
    "youtube.admin.title": "YouTube Videos",
    "youtube.admin.subtitle": "Manage YouTube video content",
    "youtube.admin.loading": "Loading...",
    "youtube.admin.addvideo": "Add Video",
    "youtube.admin.editvideo": "Edit Video",
    "youtube.admin.addnewvideo": "Add New Video",
    "youtube.admin.form.title": "Title",
    "youtube.admin.form.url": "YouTube URL",
    "youtube.admin.form.description": "Description",
    "youtube.admin.form.published": "Published",
    "youtube.admin.form.cancel": "Cancel",
    "youtube.admin.form.update": "Update",
    "youtube.admin.form.add": "Add",
    "youtube.admin.published": "Published",
    "youtube.admin.draft": "Draft",
    "youtube.admin.deleteconfirm": "Are you sure you want to delete this video?",
    "youtube.admin.updatedsuccess": "Video updated successfully",
    "youtube.admin.addedsuccess": "Video added successfully",
    "youtube.admin.deletedsuccess": "Video deleted successfully",
    "youtube.admin.saveerror": "Failed to save video",
    "youtube.admin.deleteerror": "Failed to delete video",
    "youtube.admin.novideos": "No videos yet. Add your first video to get started.",
    "youtube.page.title": "YouTube Videos",
    "youtube.page.subtitle": "Watch our latest videos and teachings",
    "youtube.page.loading": "Loading videos...",
    "youtube.page.novideos": "No videos available at this time. Check back soon!",
    
    // Contributor Request
    "contributor.title": "Become a Contributor",
    "contributor.description": "Join our team of dedicated contributors to help share faith and knowledge with our community through blog posts and Bible school content.",
    "contributor.name": "Full Name",
    "contributor.nameplaceholder": "Enter your full name",
    "contributor.email": "Email Address",
    "contributor.emailplaceholder": "your.email@example.com",
    "contributor.role": "I'm interested in contributing to",
    "contributor.roleplaceholder": "Select contribution area",
    "contributor.blogeditor": "Blog Posts",
    "contributor.bibleschooleditor": "Bible School Content",
    "contributor.both": "Both Blog and Bible School",
    "contributor.message": "Tell us about yourself",
    "contributor.messageplaceholder": "Share your experience, interests, and why you'd like to contribute...",
    "contributor.submit": "Submit Request",
    "contributor.submitting": "Submitting...",
    "contributor.success": "Request Submitted!",
    "contributor.successmessage": "Thank you for your interest! We'll review your request and get back to you soon.",
    "contributor.error": "Submission Failed",
    "contributor.errormessage": "There was an error submitting your request. Please try again.",
    "contributor.thankyou": "Thank You!",
    "contributor.reviewmessage": "We've received your contributor request and will review it soon. You'll hear from us via email.",
    "contributor.backhome": "Back to Home",
    
    // Hero
    "hero.title": "Welcome to Gods Garden Church",
    "hero.subtitle": "Growing together in faith, hope, and love",
    "hero.cta": "Join Us This Sunday",
    
    // About
    "about.title": "About Us",
    "about.subtitle": "A community rooted in faith and love",
    "about.text1": "Gods Garden Church is a vibrant, welcoming community where people from all walks of life come together to worship, grow, and serve. We believe in the transformative power of God's love and the importance of building meaningful relationships.",
    "about.text2": "Our mission is to create a nurturing environment where everyone can discover their purpose, develop their faith, and make a positive impact in our community and beyond.",
    
    // Services
    "services.title": "Service Times",
    "services.subtitle": "Join us for worship and fellowship",
    "services.sunday": "Sunday Worship",
    "services.sunday.time": "11:00 AM",
    "services.sunday.desc": "Contemporary worship service with inspiring messages",
    "services.bible": "Bible Study and Prayer",
    "services.bible.time": "Tuesdays, 7:00 PM",
    "services.bible.desc": "In-depth study of Scripture and meaningful discussion",
    "services.prayer": "Prayer Meeting",
    "services.prayer.time": "Fridays, 7:00 PM",
    "services.prayer.desc": "Come together in prayer for our church and community",
    "services.address": "4300 Racetrack Road, Rocklin, CA 95677",
    
    // Ministries
    "ministries.title": "Our Ministries",
    "ministries.subtitle": "Discover ways to grow, serve, and connect with others",
    "ministries.children": "Children's Ministry",
    "ministries.children.desc": "Nurturing young hearts with age-appropriate Bible teaching and fun activities",
    "ministries.youth": "Youth Group",
    "ministries.youth.desc": "Building strong foundations for teens through fellowship and discipleship",
    "ministries.worship": "Worship Team",
    "ministries.worship.desc": "Leading the congregation in heartfelt praise and worship",
    "ministries.outreach": "Community Outreach",
    "ministries.outreach.desc": "Serving our neighbors through various compassion programs",
    "ministries.smallgroups": "Small Groups",
    "ministries.smallgroups.desc": "Connect deeply with others through weekly Bible study and fellowship",
    "ministries.women": "Women's Ministry",
    "ministries.women.desc": "Empowering women to grow in faith through prayer, study, and friendship",
    "ministries.prayer": "Prayer Ministry",
    "ministries.prayer.desc": "Interceding for our church, community, and world through dedicated prayer",
    "ministries.missions": "Missions",
    "ministries.missions.desc": "Supporting global outreach in Guatemala and Mexico, sharing the Gospel near and far",
    
    // Contact
    "contact.title": "Get In Touch",
    "contact.subtitle": "We'd love to hear from you. Reach out with any questions or to learn more about our church",
    "contact.visit": "Visit Us",
    "contact.call": "Call Us",
    "contact.email": "Email Us",
    "contact.directions": "Get Directions",
    "contact.form.name": "Your Name",
    "contact.form.email": "Your Email",
    "contact.form.phone": "Your Phone",
    "contact.form.message": "Your Message",
    "contact.form.submit": "Send Message",
    "contact.form.sending": "Sending...",
    "contact.success.title": "Message Sent!",
    "contact.success.desc": "We'll get back to you as soon as possible.",
    "contact.error.title": "Error",
    "contact.error.desc": "Failed to send message. Please try again.",
    
    // Guatemala Mission
    "guatemala.title": "Guatemala Mission",
    "guatemala.subtitle": "Witness the transformative power of faith and service through our mission work in Guatemala. Together, we're building communities and sharing God's love.",
    "guatemala.about.title": "About Our Mission",
    "guatemala.about.text1": "Our Guatemala Mission is dedicated to serving communities in need through faith-based outreach, education, healthcare, and community development. Each year, our church sends mission teams to work alongside local churches and organizations.",
    "guatemala.about.text2": "Through partnerships with local communities, we provide essential services including medical care, educational programs, infrastructure development, and spiritual guidance. Our mission is to demonstrate God's love through practical service and lasting relationships.",
    "guatemala.about.cta": "Interested in joining our next mission trip? Contact us to learn more about upcoming opportunities.",
    "guatemala.ourwork": "Our Work in Guatemala",
    "guatemala.medicalcare": "Medical Care",
    "guatemala.medicalcare.desc": "Providing essential healthcare services, medical supplies, and health education to underserved communities throughout Guatemala.",
    "guatemala.communityoutreach": "Community Outreach",
    "guatemala.communityoutreach.desc": "Building relationships and serving communities through educational programs, food distribution, and support services.",
    "guatemala.spiritualgrowth": "Spiritual Growth",
    "guatemala.spiritualgrowth.desc": "Partnering with local churches to provide spiritual guidance, Bible distribution, and discipleship programs.",
    "guatemala.infrastructure": "Infrastructure Development",
    "guatemala.infrastructure.desc": "Constructing and renovating homes, schools, churches, and community facilities to improve living conditions.",
    "guatemala.gallery": "Photo Gallery",
    "guatemala.support": "Support Our Mission",
    
    // Mexico Mission
    "mexico.title": "Mexico Mission",
    "mexico.subtitle": "Partnering with communities in Mexico to share God's love through service, compassion, and the Gospel.",
    "mexico.about.title": "About Our Mexico Mission",
    "mexico.about.text1": "Our Mexico Mission focuses on serving communities along the border region, providing essential services, spiritual support, and developing long-term partnerships with local churches and organizations.",
    "mexico.about.text2": "We work collaboratively with Mexican communities to address immediate needs while building sustainable programs for education, healthcare, and economic development. Our approach emphasizes dignity, respect, and empowering local leaders.",
    "mexico.about.cta": "Want to be part of our next Mexico mission trip? Contact us to learn about upcoming opportunities and how you can make a difference.",
    "mexico.ourwork": "Our Work in Mexico",
    "mexico.medicalcare": "Medical Care",
    "mexico.medicalcare.desc": "Offering medical clinics, dental services, and health education to communities with limited access to healthcare.",
    "mexico.communityoutreach": "Community Outreach",
    "mexico.communityoutreach.desc": "Serving families through food programs, children's activities, vocational training, and community development initiatives.",
    "mexico.spiritualgrowth": "Spiritual Growth",
    "mexico.spiritualgrowth.desc": "Supporting local churches and ministers with resources, training, and evangelism programs to strengthen faith communities.",
    "mexico.infrastructure": "Infrastructure Development",
    "mexico.infrastructure.desc": "Building and repairing homes, water systems, and community facilities to create sustainable improvements.",
    "mexico.gallery": "Photo Gallery",
    "mexico.support": "Support Our Mission",
    
    
    // Ministry Pages Common
    "ministry.getinvolved": "Get Involved",
    "ministry.contactus": "Contact Us",
    "ministry.about": "About",
    "ministry.details": "Program Details",
    "ministry.whenwemeet": "When We Meet",
    "ministry.time": "Time",
    "ministry.agerange": "Age Range",
    "ministry.agegroups": "Age Groups",
    "ministry.location": "Location",
    "ministry.activities": "Activities",
    
    // Children's Ministry
    "children.about.text1": "Our Children's Ministry provides a safe, loving, and engaging environment where kids can learn about Jesus and grow in their faith. Through interactive lessons, worship, games, and crafts, we help children understand God's love and develop a personal relationship with Him.",
    "children.about.text2": "We offer age-appropriate programs for infants through elementary school, with dedicated teachers who are passionate about investing in the next generation.",
    "children.involved": "Whether you're a parent looking to enroll your child or someone interested in volunteering, we'd love to hear from you! Contact us to learn more about our Children's Ministry.",
    
    // Youth Group
    "youth.about.text1": "Our Youth Group is a vibrant community where middle and high school students can grow in their faith, build meaningful friendships, and discover their purpose in Christ. Through engaging discussions, worship, games, and service projects, we create an environment where teens feel welcome and valued.",
    "youth.about.text2": "We tackle real-life issues, explore Scripture together, and encourage students to develop a personal, authentic relationship with Jesus that will sustain them through their teenage years and beyond.",
    "youth.whatwedo": "What We Do",
    "youth.involved": "All middle and high school students are welcome! Contact us to learn more about joining our youth group or volunteer opportunities for adult leaders.",
    
    // Worship Team
    "worship.about.text1": "Our Worship Team is passionate about creating an atmosphere where people can encounter God's presence through music and song. We lead worship every Sunday, using contemporary and traditional songs to help our congregation express their love and devotion to Jesus.",
    "worship.about.text2": "More than just performing music, we strive to be worship leaders who guide others into a deeper connection with God. Our team includes vocalists, instrumentalists, and tech crew members who work together to create meaningful worship experiences.",
    "worship.practice": "Practice & Service Times",
    "worship.practice.day": "Practice",
    "worship.practice.days": "Thursdays",
    "worship.practice.time": "7:00 PM - 9:00 PM",
    "worship.service": "Service",
    "worship.service.days": "Sundays",
    "worship.service.time": "11:00 AM Worship Service",
    "worship.positions": "Team Positions",
    "worship.join": "Join Our Team",
    "worship.join.text": "If you have a heart for worship and musical talent, we'd love to have you audition for our team. We welcome musicians, vocalists, and tech volunteers of all skill levels. Contact us to schedule an audition or learn more about serving on the worship team.",
    
    // Community Outreach
    "outreach.about.text1": "Community Outreach is at the heart of our mission to love and serve others as Jesus did. We believe that faith is demonstrated through action, and we're committed to making a tangible difference in the lives of those around us.",
    "outreach.about.text2": "Through various programs and partnerships, we provide practical help, emotional support, and spiritual encouragement to individuals and families in need throughout our community.",
    "outreach.programs": "Our Programs",
    "outreach.involved.text": "Whether you can volunteer your time, donate resources, or simply pray for our outreach efforts, there's a place for you to make a difference. Contact us to learn more about current needs and opportunities.",
    
    // Small Groups
    "smallgroups.about.text1": "Small Groups are the heart of our church community. These intimate gatherings provide a space where you can build authentic relationships, grow spiritually, and find support through life's ups and downs. It's where the church truly becomes family.",
    "smallgroups.about.text2": "Each group meets weekly in homes throughout the Rocklin area to study God's Word, pray together, share life, and encourage one another in faith. Whether you're new to church or a longtime believer, you'll find a welcoming community in our small groups.",
    "smallgroups.expect": "What to Expect",
    "smallgroups.where": "Where",
    "smallgroups.groupsize": "Group Size",
    "smallgroups.format": "Format",
    "smallgroups.available": "Available Groups",
    "smallgroups.join": "Join a Small Group",
    "smallgroups.join.text": "Ready to take the next step in your spiritual journey? Contact us to find the perfect small group for you. We'll help match you with a group that fits your schedule, stage of life, and interests.",
    
    // Women's Ministry
    "women.about.text1": "Our Women's Ministry exists to create a safe, supportive community where women of all ages and life stages can connect with God and each other. We believe that when women are spiritually strong and emotionally healthy, they impact their families, workplaces, and communities in powerful ways.",
    "women.about.text2": "Through Bible studies, prayer gatherings, special events, and service opportunities, we encourage women to discover their identity in Christ, develop their gifts, and live out their God-given purpose.",
    "women.gatherings": "Regular Gatherings",
    "women.biblestudy": "Bible Study",
    "women.biblestudy.desc": "In-depth study of Scripture with discussion, prayer, and coffee. Childcare available.",
    "women.prayer": "Prayer Circle",
    
    // Prayer Ministry
    "prayer.about.text1": "Prayer is the foundation of everything we do at Gods Garden Church. Our Prayer Ministry is dedicated to lifting up the needs of our congregation, community, and world before the throne of God. We believe in the power of prayer to transform lives, heal brokenness, and move mountains.",
    "prayer.about.text2": "Our prayer team is committed to faithful intercession and creating spaces where people can experience God's presence and power through prayer. Whether you need prayer or want to join others in praying, we're here for you.",
    "prayer.opportunities": "Prayer Opportunities",
    "prayer.submit": "Submit a Prayer Request",
    "prayer.submit.text": "No matter what you're facing, we would be honored to pray for you. Share your prayer request with us, and our prayer team will lift you up in prayer. All requests are kept confidential unless you give permission to share with the congregation.",
    "prayer.join": "Join the Prayer Team",
    "prayer.join.text": "If you have a heart for prayer and want to serve through intercession, we invite you to join our Prayer Ministry team. Contact us to learn about opportunities to serve in prayer meetings, the prayer chain, or prayer room ministry.",
    
    // Missions Page
    "missions.title": "Missions",
    "missions.subtitle": "Supporting global outreach and missions work, sharing the Gospel near and far",
    "missions.heart.title": "Our Mission Heart",
    "missions.heart.text1": "At Gods Garden Church, we believe that the Great Commission calls us to go and make disciples of all nations. Our missions program reflects this commitment through strategic partnerships, short-term mission trips, and long-term support of missionaries and ministries around the world.",
    "missions.heart.text2": "We focus on sustainable, relationship-based missions that empower local churches and communities while sharing the hope of Jesus Christ through both word and deed.",
    "missions.fields.title": "Our Mission Fields",
    "missions.guatemala.title": "Guatemala Mission",
    "missions.guatemala.desc": "Partnering with communities to provide education, healthcare, infrastructure development, and spiritual guidance throughout rural Guatemala.",
    "missions.guatemala.cta": "View Photo Gallery →",
    "missions.mexico.title": "Mexico Mission",
    "missions.mexico.desc": "Serving border communities with compassion, providing essential services, and building sustainable programs for long-term impact.",
    "missions.mexico.cta": "View Photo Gallery →",
    "missions.serve.title": "How We Serve",
    "missions.serve.compassion": "Compassion Ministries",
    "missions.serve.compassion.desc": "Medical care, food distribution, and disaster relief",
    "missions.serve.community": "Community Development",
    "missions.serve.community.desc": "Education, infrastructure, and economic empowerment",
    "missions.serve.partnerships": "Church Partnerships",
    "missions.serve.partnerships.desc": "Supporting local pastors and church planting efforts",
    "missions.serve.evangelism": "Evangelism & Discipleship",
    "missions.serve.evangelism.desc": "Sharing the Gospel and training leaders",
    "missions.involved.title": "Ways to Get Involved",
    "missions.involved.trip": "Join a Mission Trip:",
    "missions.involved.trip.desc": "Experience short-term missions firsthand",
    "missions.involved.pray": "Pray:",
    "missions.involved.pray.desc": "Partner with us through consistent prayer for missionaries and communities",
    "missions.involved.give": "Give:",
    "missions.involved.give.desc": "Support mission projects financially through designated giving",
    "missions.involved.sponsor": "Sponsor:",
    "missions.involved.sponsor.desc": "Partner with a missionary or mission project long-term",
    "missions.involved.text": "Whether God is calling you to go, give, or pray, there's a place for you in our missions ministry. Contact us to learn about current opportunities and upcoming mission trips.",
    "missions.involved.cta": "Contact Us",
    
    // Donate Page
    "donate.title": "Support God's Garden Church",
    "donate.subtitle": "Your generous donations help our church to grow, provide supplies and support to our organization. Every contribution makes a lasting impat in the lives of those we serve",
    "donate.support.title": "Support Ministry",
    "donate.support.description": "Help fund programs and ministries",
    "donate.missions.title": "Global Missions",
    "donate.missions.description": "Support our work around the world",
    "donate.community.title": "Community Outreach",
    "donate.community.description": "Serve those in need locally",
    
    // Sermons Page
    "sermons.title": "Sermons",
    "sermons.subtitle": "Listen to past messages and grow in faith",
    "sermons.loading": "Loading sermons...",
    "sermons.nosermons": "No sermons available yet",
    "sermons.speaker": "Speaker",
    "sermons.date": "Date",
    "sermons.scripture": "Scripture",
    "sermons.audio": "Audio",
    "sermons.video": "Video",
    "sermons.backToSermons": "Back to Sermons",
    "sermons.description": "Description",
    "sermons.error.title": "Error",
    "sermons.error.description": "Failed to load sermons",
    "nav.sermons": "Sermons",
    
    // Bible Study
    "nav.biblestudy": "Bible Study",
    "nav.bible": "Bible",
    "biblestudy.title": "Bible Study",
    "biblestudy.subtitle": "Explore Scripture with AI assistance, verse lookup, and study topics",
    "biblestudy.chat.title": "Ask Bible Questions",
    "biblestudy.chat.description": "Get answers about Scripture, theology, and practical application",
    "biblestudy.chat.placeholder": "Ask a Bible question...",
    "biblestudy.chat.empty": "Ask a question to start your Bible study",
    "biblestudy.chat.tryasking": "Try asking:",
    "biblestudy.verse.title": "Verse Lookup",
    "biblestudy.verse.description": "Enter a Bible reference to view the full text",
    "biblestudy.verse.placeholder": "e.g., John 3:16 or Psalm 23",
    "biblestudy.verse.popular": "Popular Verses",
    "biblestudy.topics.title": "Study Topics",
    "biblestudy.topics.description": "Explore key themes in Scripture",
    "biblestudy.topics.ask": "Ask AI about",
    "biblestudy.tab.chat": "AI Q&A",
    "biblestudy.tab.verse": "Verse Lookup",
    "biblestudy.tab.topics": "Study Topics",
    "biblestudy.topics.faith": "Faith & Trust",
    "biblestudy.topics.love": "Love & Compassion",
    "biblestudy.topics.prayer": "Prayer & Worship",
    "biblestudy.topics.salvation": "Salvation & Grace",
    "biblestudy.topics.wisdom": "Wisdom & Guidance",
    "biblestudy.topics.peace": "Peace & Comfort",
    
    // Special Events
    "specialevents.title": "Special Events",
    "specialevents.description": "Celebrate God's faithfulness through our church's special events and memorable moments",
    "specialevents.gallery": "Event Gallery",
    "specialevents.noevents": "No special events to display yet",
    "common.loading": "Loading...",
    "blog.title": "Church Blog",
    "blog.subtitle": "Devotionals, teachings, and updates from our community",
    "blog.loading": "Loading posts...",
    "blog.noPosts": "No blog posts available yet",
    "blog.searchPlaceholder": "Search blog posts...",
    "blog.allCategories": "All Categories",
    "blog.backToBlog": "Back to Blog",
    "blog.error": "Error",
    "blog.errorDescription": "Failed to load blog posts",
    "blog.postNotFound": "Post not found",
    "blog.addPost": "Add Post",
    "blog.contribute.title": "Want to Contribute?",
    "blog.contribute.description": "Join our team of writers and share your spiritual insights, teachings, and testimonies with our community.",
    "blog.contribute.button": "Become a Contributor",
    
    // Daily Bible Reading
    "dailyreading.title": "Daily Bible Reading",
    "dailyreading.subtitle": "A year-long journey through God's Word. Follow this reading plan to read through the Bible in a systematic way.",
    "dailyreading.oldtestament": "Old Testament Reading",
    "dailyreading.newtestament": "New Testament Reading",
    "dailyreading.psalm": "Psalm",
    "dailyreading.proverb": "Proverb",
    "dailyreading.gototoday": "Go to Today",
    "dailyreading.about.title": "About This Reading Plan",
    "dailyreading.about.text1": "This daily reading plan guides you through the entire Bible in a year, with selections from the Old Testament, New Testament, Psalms, and Proverbs each day.",
    "dailyreading.about.benefits": "Benefits of Daily Bible Reading:",
    "dailyreading.about.benefit1": "Deepens your relationship with God",
    "dailyreading.about.benefit2": "Provides wisdom and guidance for daily living",
    "dailyreading.about.benefit3": "Strengthens your faith and understanding",
    "dailyreading.about.benefit4": "Creates a consistent spiritual discipline",
    "dailyreading.about.verse": "\"Your word is a lamp to my feet and a light to my path.\" - Psalm 119:105",
    
    // Bible School
    "bibleschool.title": "Bible School",
    "bibleschool.subtitle": "Deepen your understanding of Scripture through structured courses",
    "bibleschool.welcome.title": "Welcome to Bible School",
    "bibleschool.welcome.text1": "Our Bible School offers comprehensive courses designed to help you grow in your understanding of God's Word and develop your gifts for ministry.",
    "bibleschool.welcome.text2": "Whether you're new to the faith or seeking to deepen your theological knowledge, our courses provide solid biblical teaching in a supportive environment.",
    "bibleschool.loading": "Loading courses...",
    "bibleschool.noCourses": "No courses available at this time",
    "bibleschool.instructor": "Instructor",
    "bibleschool.duration": "Duration",
    "bibleschool.schedule": "Schedule",
    "bibleschool.maxStudents": "Max Students",
    "bibleschool.addCourse": "Add Course",
    "bibleschool.contact.title": "Interested in Enrolling?",
    "bibleschool.contact.text": "If you're interested in any of our courses, please contact us for more information about enrollment, schedule, and requirements.",
    "bibleschool.contact.info": "Email us or speak with one of our ministry leaders after service.",
    "bibleschool.error": "Error",
    "bibleschool.errorDescription": "Failed to load courses",
    "bibleschool.contribute.title": "Want to Teach or Share Knowledge?",
    "bibleschool.contribute.description": "Share your Scripture knowledge and teaching experience with our students. We're looking for dedicated teachers for our Bible School.",
    "bibleschool.contribute.button": "Become an Instructor",
    
    // Admin Dashboard
    "admin.title": "Admin Dashboard",
    "admin.error": "Error",
    "admin.success": "Success",
    "admin.accessdenied": "Access denied. Admin or viewer access required.",
    "admin.viewermode": "Viewer Mode",
    "admin.tabs.donations": "Donations",
    "admin.tabs.announcements": "Announcements",
    "admin.tabs.sermons": "Sermons",
    "admin.tabs.blog": "Blog",
    "admin.tabs.bibleschool": "Bible School",
    "admin.tabs.bibleschool.gallery": "Bible School Gallery",
    "admin.tabs.photos": "Mission Photos",
    "admin.tabs.specialevents": "Special Events",
    "admin.specialevents.title": "Special Events",
    "admin.specialevents.createnew": "Create New Event",
    "admin.specialevents.loading": "Loading events...",
    "admin.specialevents.noevents": "No events yet. Create your first event!",
    "admin.specialevents.gallery": "Event Media Gallery",
    "admin.specialevents.selectmultiple": "Select Multiple",
    "admin.specialevents.cancelselection": "Cancel Selection",
    "admin.specialevents.selectall": "Select All",
    "admin.specialevents.deselectall": "Deselect All",
    "admin.specialevents.delete": "Delete",
    "admin.specialevents.uploadmedia": "Upload Media",
    "admin.specialevents.loadingmedia": "Loading media...",
    "admin.specialevents.nomedia": "No media yet. Upload photos or videos to this event!",
    "admin.specialevents.edit": "Edit",
    "admin.specialevents.order": "Order",
    "admin.specialevents.type": "Type",
    "admin.specialevents.editevent": "Edit Event",
    "admin.specialevents.createevent": "Create New Event",
    "admin.specialevents.updateevent": "Update event details",
    "admin.specialevents.createeventdesc": "Create a new event gallery",
    "admin.specialevents.eventtitle": "Event Title",
    "admin.specialevents.description": "Description",
    "admin.specialevents.descriptionplaceholder": "Brief description of the event",
    "admin.specialevents.eventdate": "Event Date",
    "admin.specialevents.displayorder": "Display Order",
    "admin.specialevents.cancel": "Cancel",
    "admin.specialevents.update": "Update Event",
    "admin.specialevents.create": "Create Event",
    "admin.specialevents.editmedia": "Edit Media",
    "admin.specialevents.uploadmediatitle": "Upload Media",
    "admin.specialevents.updatemedia": "Update media details",
    "admin.specialevents.uploadmediadesc": "Upload photos or videos to this event. Supported formats: JPG, PNG, WebP, MP4, MOV, AVI, WebM",
    "admin.specialevents.youtubeurl": "YouTube Video URL (Optional)",
    "admin.specialevents.youtubeurlplaceholder": "https://www.youtube.com/watch?v=... or https://youtu.be/...",
    "admin.specialevents.youtubehelp": "If you provide a YouTube URL, you can optionally upload a custom thumbnail image below.",
    "admin.specialevents.thumbnailimage": "Thumbnail Image (Optional)",
    "admin.specialevents.selectfiles": "Select Files (Images & Videos)",
    "admin.specialevents.customthumbnail": "Upload a custom thumbnail for your YouTube video (optional)",
    "admin.specialevents.filesselected": "file(s) selected:",
    "admin.specialevents.uploading": "Uploading:",
    "admin.specialevents.mediatitle": "Title",
    "admin.specialevents.mediatitlerequired": "Title is required for each item",
    "admin.specialevents.mediadescription": "Description (Optional)",
    "admin.specialevents.mediadescriptionplaceholder": "Add a description for this media",
    "admin.specialevents.mediaorder": "Display Order",
    "admin.specialevents.savemedia": "Save Media",
    "admin.specialevents.date": "Date",
    "admin.tabs.users": "Users",
    "admin.tabs.members": "Members",
    "admin.tabs.contributors": "Contributor Requests",
    "admin.tabs.content": "Site Content",
    "admin.donations.totalreceived": "Total Received",
    "admin.donations.netdonations": "Net Donations",
    "admin.donations.processingfees": "Processing Fees",
    "admin.donations.includingfees": "Including processing fees",
    "admin.donations.excludingfees": "Excluding processing fees",
    "admin.donations.stripefees": "PayPal processing fees",
    "admin.donations.filtercategory": "Filter by Category:",
    "admin.donations.allcategories": "All Categories",
    "admin.donations.recent": "Recent Donations",
    "admin.donations.loading": "Loading donations...",
    "admin.donations.nodonations": "No donations found.",
    "admin.donations.date": "Date",
    "admin.donations.donor": "Donor",
    "admin.donations.email": "Email",
    "admin.donations.category": "Category",
    "admin.donations.donation": "Donation",
    "admin.donations.processingfee": "Processing Fee",
    "admin.donations.total": "Total",
    "admin.donations.actions": "Actions",
    "admin.category.guatemala": "Guatemala Mission",
    "admin.category.mexico": "Mexico Mission",
    
    "admin.category.church": "Church Donation",
    "admin.category.tithe": "Tithe",
    "admin.users.title": "User Management",
    "admin.users.loading": "Loading users...",
    "admin.users.nousers": "No users found.",
    "admin.users.email": "Email",
    "admin.users.role": "Role",
    "admin.users.roles": "Roles & Management",
    "admin.users.created": "Created",
    "admin.users.lastsignin": "Last Sign In",
    "admin.users.never": "Never",
    "admin.users.actions": "Actions",
    "admin.users.admin": "Admin",
    "admin.users.viewer": "Viewer",
    "admin.users.primary": "Primary",
    "admin.users.user": "User",
    "admin.users.norole": "No role assigned",
    "admin.users.deleteconfirm": "Are you sure you want to delete user",
    "admin.users.cannotdelete": "Cannot delete primary admin",
    "admin.users.deleteuser": "Delete User",
    "admin.users.yourself": "You (cannot delete yourself)",
    "admin.users.makeadmin": "Make Admin",
    "admin.users.makeviewer": "Make Viewer",
    "admin.users.removeadmin": "Remove Admin",
    "admin.users.removeviewer": "Remove Viewer",
    "admin.users.roleassigned": "Role assigned successfully",
    "admin.users.roleremoved": "Role removed successfully",
    "admin.users.roleerror": "Failed to update role",
    "admin.users.createuser": "Create User",
    "admin.users.createnew": "Create New User",
    "admin.users.createdescription": "Add a new user to the system and optionally assign them a role",
    "admin.users.password": "Password",
    "admin.users.passwordhint": "Minimum 6 characters",
    "admin.users.assignrole": "Assign Role (Optional)",
    "admin.users.cancel": "Cancel",
    "admin.users.create": "Create",
    "admin.users.createsuccess": "User created successfully",
    "admin.users.createerror": "Failed to create user",
    
    // Admin Contributor Requests
    "admin.contributors.title": "Contributor Requests",
    "admin.contributors.loading": "Loading requests...",
    "admin.contributors.norequests": "No contributor requests found",
    "admin.contributors.name": "Name",
    "admin.contributors.email": "Email",
    "admin.contributors.role": "Requested Role",
    "admin.contributors.message": "Message",
    "admin.contributors.status": "Status",
    "admin.contributors.created": "Submitted",
    "admin.contributors.actions": "Actions",
    "admin.contributors.field.name": "Name",
    "admin.contributors.field.email": "Email",
    "admin.contributors.field.role": "Requested Role",
    "admin.contributors.field.message": "Message",
    "admin.contributors.field.status": "Status",
    "admin.contributors.field.date": "Date",
    "admin.contributors.status.all": "All",
    "admin.contributors.status.pending": "Pending",
    "admin.contributors.status.approved": "Approved",
    "admin.contributors.status.rejected": "Rejected",
    "admin.contributors.roles.blogeditor": "Blog Editor",
    "admin.contributors.roles.bibleschooleditor": "Bible School Editor",
    "admin.contributors.roles.both": "Both (Blog + Bible School)",
    "admin.contributors.pending": "Pending",
    "admin.contributors.approved": "Approved and invited successfully",
    "admin.contributors.rejected": "Rejected successfully",
    "admin.contributors.approve": "Approve and Create User",
    "admin.contributors.reject": "Reject",
    "admin.contributors.delete": "Delete",
    "admin.contributors.approvesuccess": "Request approved and user invited",
    "admin.contributors.approveerror": "Failed to approve request",
    "admin.contributors.rejectsuccess": "Request rejected",
    "admin.contributors.rejecterror": "Failed to reject request",
    "admin.contributors.deletesuccess": "Request deleted",
    "admin.contributors.deleteerror": "Failed to delete request",
    "admin.contributors.deleteconfirm": "Are you sure you want to delete this request?",
    "admin.contributors.rejectconfirm": "Are you sure you want to reject this request?",
    "admin.contributors.nomessage": "No message provided",
    
    "admin.sermons.title": "Manage Sermons",
    "admin.sermons.addsermon": "Add Sermon",
    "admin.sermons.editsermon": "Edit Sermon",
    "admin.sermons.addnew": "Add New Sermon",
    "admin.sermons.filldetails": "Fill in the sermon details below",
    "admin.sermons.title.label": "Title",
    "admin.sermons.speaker.label": "Speaker",
    "admin.sermons.date.label": "Date",
    "admin.sermons.scripture.label": "Scripture Reference",
    "admin.sermons.scripture.placeholder": "e.g., John 3:16",
    "admin.sermons.description.label": "Description",
    "admin.sermons.audiourl.label": "Audio URL",
    "admin.sermons.audiourl.placeholder": "https://example.com/audio.mp3",
    "admin.sermons.videourl.label": "Video URL",
    "admin.sermons.videourl.placeholder": "https://youtube.com/watch?v=...",
    "admin.sermons.cancel": "Cancel",
    "admin.sermons.create": "Create",
    "admin.sermons.update": "Update",
    "admin.sermons.loading": "Loading sermons...",
    "admin.sermons.nosermons": "No sermons yet. Click \"Add Sermon\" to create one.",
    "admin.sermons.deleteconfirm": "Are you sure you want to delete this sermon?",
    "admin.sermons.media": "Media",
    "admin.sermons.mediatype.audio": "Audio",
    "admin.sermons.mediatype.video": "Video",
    "admin.sermons.translations": "Manage Translations",
    "admin.sermons.translations.title": "Sermon Translations",
    "admin.sermons.translations.description": "Add translations for this sermon in different languages",
    "admin.sermons.translations.language": "Language",
    "admin.sermons.translations.english": "English",
    "admin.sermons.translations.russian": "Russian",
    "admin.sermons.translations.spanish": "Spanish",
    "admin.sermons.translations.save": "Save Translations",
    "admin.sermons.translations.success": "Translations saved successfully",
    "admin.sermons.translations.error": "Failed to save translations",
    "admin.sermons.updatedsuccess": "Sermon updated successfully.",
    "admin.sermons.addedsuccess": "Sermon added successfully.",
    "admin.sermons.deletedsuccess": "Sermon deleted successfully.",
    "admin.sermons.saveerror": "Failed to save sermon.",
    "admin.sermons.fetcherror": "Failed to load sermons.",
    "admin.sermons.deleteerror": "Failed to delete sermon.",
    "admin.sermons.published": "Published",
    
    // Admin - Announcements
    "admin.announcements.title": "Manage Announcements",
    "admin.announcements.add": "Add Announcement",
    "admin.announcements.edit": "Edit Announcement",
    "admin.announcements.editdesc": "Update the announcement details below.",
    "admin.announcements.adddesc": "Fill in the details for the new announcement.",
    "admin.announcements.titlefield": "Title",
    "admin.announcements.titleplaceholder": "Announcement title",
    "admin.announcements.content": "Content",
    "admin.announcements.contentplaceholder": "Announcement content",
    "admin.announcements.published": "Publish announcement",
    "admin.announcements.create": "Create",
    "admin.announcements.update": "Update",
    "admin.announcements.cancel": "Cancel",
    "admin.announcements.loading": "Loading announcements...",
    "admin.announcements.noannouncements": "No announcements yet. Create your first announcement.",
    "admin.announcements.deleteconfirm": "Are you sure you want to delete this announcement?",
    "admin.announcements.translations": "Manage Translations",
    "admin.announcements.translationsdesc": "Translate \"{title}\" to other languages",
    "admin.announcements.translations.russian": "Russian (RU)",
    "admin.announcements.translations.spanish": "Spanish (ES)",
    "admin.announcements.translations.title": "Title",
    "admin.announcements.translations.content": "Content",
    "admin.announcements.translations.save": "Save Translations",
    "admin.announcements.translations.cancel": "Cancel",
    "admin.announcements.status.published": "Published",
    "admin.announcements.status.draft": "Draft",
    "admin.announcements.updatedsuccess": "Announcement updated successfully",
    "admin.announcements.addedsuccess": "Announcement created successfully",
    "admin.announcements.deletedsuccess": "Announcement deleted successfully",
    "admin.announcements.saveerror": "Failed to save announcement",
    "admin.announcements.fetcherror": "Failed to load announcements",
    "admin.announcements.deleteerror": "Failed to delete announcement",
    "admin.announcements.translationssuccess": "Translations saved successfully",
    "admin.announcements.translationserror": "Failed to save translations",
    
    "admin.users.deletedsuccess": "User deleted successfully.",
    "admin.users.deleteerror": "Failed to delete user.",
    "admin.users.fetcherror": "Failed to load users.",
    "admin.donations.deletedsuccess": "Donation deleted successfully.",
    "admin.donations.deleteerror": "Failed to delete donation.",
    "admin.donations.fetcherror": "Failed to load donations.",
    "admin.photos.title": "Mission Photos",
    "admin.photos.upload": "Upload Photo",
    "admin.photos.uploadnew": "Upload New Photo",
    "admin.photos.edit": "Edit Photo",
    "admin.photos.description": "Add photos to mission galleries",
    "admin.photos.file": "Photo File",
    "admin.photos.phototitle": "Photo Title",
    "admin.photos.photodescription": "Photo Description",
    "admin.photos.displayorder": "Display Order",
    "admin.photos.order": "Order",
    "admin.photos.cancel": "Cancel",
    "admin.photos.uploadbutton": "Upload",
    "admin.photos.update": "Update",
    "admin.photos.editbutton": "Edit",
    "admin.photos.deletebutton": "Delete",
    "admin.photos.loading": "Loading photos...",
    "admin.photos.nophotos": "No photos found.",
    "admin.photos.fetcherror": "Failed to load photos.",
    "admin.photos.uploadsuccess": "Photo uploaded successfully.",
    "admin.photos.uploaderror": "Failed to upload photo.",
    "admin.photos.updatesuccess": "Photo updated successfully.",
    "admin.photos.updateerror": "Failed to update photo.",
    "admin.photos.deletesuccess": "Photo deleted successfully.",
    "admin.photos.deleteerror": "Failed to delete photo.",
    "admin.photos.deleteconfirm": "Are you sure you want to delete this photo?",
    "admin.photos.nofile": "Please select a file to upload.",
    
    // Admin Bible School
    "admin.bibleschool.title": "Bible School Management",
    "admin.bibleschool.add": "Add Course",
    "admin.bibleschool.create": "Create Course",
    "admin.bibleschool.edit": "Edit Course",
    "admin.bibleschool.update": "Update Course",
    "admin.bibleschool.cancel": "Cancel",
    "admin.bibleschool.loading": "Loading courses...",
    "admin.bibleschool.nocourses": "No courses found.",
    "admin.bibleschool.filter.all": "All",
    "admin.bibleschool.filter.published": "Published",
    "admin.bibleschool.filter.draft": "Draft",
    "admin.bibleschool.field.title": "Title",
    "admin.bibleschool.field.description": "Description",
    "admin.bibleschool.field.instructor": "Instructor",
    "admin.bibleschool.field.duration": "Duration",
    "admin.bibleschool.field.duration.placeholder": "e.g., 8 weeks, 3 months",
    "admin.bibleschool.field.level": "Level",
    "admin.bibleschool.field.level.placeholder": "e.g., Beginner, Intermediate, Advanced",
    "admin.bibleschool.field.schedule": "Schedule",
    "admin.bibleschool.field.schedule.placeholder": "e.g., Mondays 7:00 PM",
    "admin.bibleschool.field.maxstudents": "Max Students",
    "admin.bibleschool.field.image": "Image",
    "admin.bibleschool.field.published": "Published",
    "admin.bibleschool.status": "Status",
    "admin.bibleschool.actions": "Actions",
    "admin.bibleschool.translations": "Translations",
    "admin.bibleschool.status.published": "Published",
    "admin.bibleschool.status.draft": "Draft",
    
    // Admin Blog
    "admin.blog.title": "Blog Management",
    "admin.blog.add": "Add Blog Post",
    "admin.blog.create": "Create Blog Post",
    "admin.blog.edit": "Edit Blog Post",
    "admin.blog.update": "Update Blog Post",
    "admin.blog.cancel": "Cancel",
    "admin.blog.loading": "Loading blog posts...",
    "admin.blog.noposts": "No blog posts found.",
    "admin.blog.filter.all": "All",
    "admin.blog.filter.published": "Published",
    "admin.blog.filter.draft": "Draft",
    "admin.blog.field.title": "Title",
    "admin.blog.field.slug": "Slug",
    "admin.blog.field.slug.placeholder": "auto-generated-from-title",
    "admin.blog.field.author": "Author",
    "admin.blog.field.category": "Category",
    "admin.blog.field.excerpt": "Excerpt",
    "admin.blog.field.content": "Content",
    "admin.blog.field.image": "Featured Image URL",
    "admin.blog.field.published": "Published",
    "admin.blog.status": "Status",
    "admin.blog.actions": "Actions",
    "admin.blog.translations": "Translations",
    "admin.blog.translations.title": "Blog Post Translations",
    "admin.blog.translations.desc": "Add translations for this blog post in different languages",
    "admin.blog.translations.save": "Save Translations",
    "admin.blog.status.published": "Published",
    "admin.blog.status.draft": "Draft",
    
    // Membership
    "membership.title": "Church Membership",
    "membership.subtitle": "Join our church family and become an active part of our community",
    "membership.benefit1.title": "Community Connection",
    "membership.benefit1.description": "Connect with other members and build lasting relationships",
    "membership.benefit2.title": "Active Participation",
    "membership.benefit2.description": "Engage in church activities and ministries",
    "membership.benefit3.title": "Convenient Giving",
    "membership.benefit3.description": "Easy online tithe and dues payment tracking",
    "membership.notloggedin": "Sign in or create an account to begin your membership application",
    "membership.signinsignup": "Sign In / Sign Up",
    "membership.gotoportal": "Go to Member Portal",
    "membership.readytojoin": "Ready to join our church family?",
    "membership.becomemember": "Become a Member",
    "membership.whattoexpect": "What to Expect",
    "membership.step1": "Complete a simple registration form",
    "membership.step2": "Provide basic contact information",
    "membership.step3": "Your application will be reviewed by church administration",
    "membership.step4": "Once approved, you'll have access to the member portal",
    "membership.step5": "Make tithes and dues payments online",
    "membership.step6": "Connect with other church members",
    
    // Member Portal
    "member.portal.title": "Member Portal",
    "member.portal.welcome": "Welcome!",
    "member.portal.needregistration": "You need to complete your member registration to access the portal.",
    "member.portal.completeregistration": "Complete Registration",
    "member.portal.tabs.profile": "Profile",
    "member.portal.tabs.payments": "Payments",
    "member.portal.tabs.family": "Family",
    "member.portal.tabs.security": "Security",
    
    // Member Profile
    "member.profile.title": "Member Profile",
    "member.profile.description": "View and update your information",
    "member.profile.edit": "Edit Profile",
    "member.profile.fullName": "Full Name",
    "member.profile.email": "Email",
    "member.profile.phone": "Phone",
    "member.profile.address": "Address",
    "member.profile.numberOfKids": "Number of Children",
    "member.profile.notProvided": "Not provided",
    "member.profile.memberSince": "Member Since",
    "member.profile.saveChanges": "Save Changes",
    "member.profile.saving": "Saving...",
    "member.profile.cancel": "Cancel",
    "member.profile.success": "Success",
    "member.profile.updateSuccess": "Profile updated successfully",
    "member.profile.error": "Error",
    "member.profile.updateError": "Failed to update profile",
    
    // Member Payments
    "member.payments.makePayment": "Make a Payment",
    "member.payments.submitPayment": "Submit tithe or monthly dues",
    "member.payments.tithe": "Tithe",
    "member.payments.titheDescription": "Submit your tithe offering",
    "member.payments.payTithe": "Pay Tithe",
    "member.payments.monthlyDues": "Monthly Dues",
    "member.payments.monthlyRentalDues": "Monthly Rental Dues",
    "member.payments.duesDescription": "Pay your monthly membership dues",
    "member.payments.payDues": "Pay Monthly Dues",
    "member.payments.payMonthlyRentalDues": "Pay Monthly Rental Dues",
    "member.payments.duesSchedule": "Monthly Dues Schedule",
    "member.payments.monthlyPaymentSchedule": "Monthly Payment Schedule",
    "member.payments.scheduleAmount": "$30 each church member",
    "member.payments.history": "Payment History",
    "member.payments.historyDescription": "View all your past payments",
    "member.payments.loading": "Loading...",
    "member.payments.noHistory": "No payment history yet",
    "member.payments.date": "Date",
    "member.payments.type": "Type",
    "member.payments.amount": "Amount",
    "member.payments.status": "Status",
    "member.payments.error": "Error",
    "member.payments.loadError": "Failed to load payment history",
    
    // MFA
    "mfa.title": "Two-Factor Authentication",
    "mfa.enable": "Enable Two-Factor Authentication",
    "mfa.disable": "Disable Two-Factor Authentication",
    "mfa.setupDescription": "Add an extra layer of security to your account",
    "mfa.setupExplanation": "Two-factor authentication (2FA) adds an additional layer of security by requiring a verification code from your phone when you sign in.",
    "mfa.authenticatorRequired": "You'll need an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.",
    "mfa.setupButton": "Set Up Two-Factor Authentication",
    "mfa.protectedDescription": "Your account is protected with 2FA",
    "mfa.enabledMessage": "Two-factor authentication is enabled. You'll need your authenticator app when signing in.",
    
    // Family Management
    "member.family.title": "Family Relationships",
    "member.family.description": "Manage your family connections",
    "member.family.addRelationship": "Add Relationship",
    "member.family.addTitle": "Add Family Relationship",
    "member.family.addDescription": "Connect with another church member",
    "member.family.churchMember": "Church Member",
    "member.family.selectMember": "Select a member",
    "member.family.relationshipType": "Relationship Type",
    "member.family.selectRelationship": "Select relationship",
    "member.family.spouse": "Spouse",
    "member.family.parent": "Parent",
    "member.family.child": "Child",
    "member.family.sibling": "Sibling",
    "member.family.addButton": "Add Relationship",
    "member.family.loading": "Loading...",
    "member.family.noRelationships": "No family relationships added yet",
    "member.family.name": "Name",
    "member.family.relationship": "Relationship",
    "member.family.actions": "Actions",
    "member.family.success": "Success",
    "member.family.addSuccess": "Family relationship added",
    "member.family.removeSuccess": "Relationship removed",
    "member.family.error": "Error",
    "member.family.loadError": "Failed to load family relationships",
    "member.family.selectError": "Please select a member and relationship type",
    "member.family.addError": "Failed to add relationship",
    "member.family.removeError": "Failed to remove relationship",
    
    // Admin Member Management
    "admin.members.title": "Member Management",
    "admin.members.directory.title": "Member Directory",
    "admin.members.directory.description": "View and manage all church members",
    "admin.members.directory.loading": "Loading...",
    "admin.members.directory.name": "Name",
    "admin.members.directory.email": "Email",
    "admin.members.directory.phone": "Phone",
    "admin.members.directory.status": "Status",
    "admin.members.directory.children": "Children",
    "admin.members.directory.actions": "Actions",
    "admin.members.directory.na": "N/A",
    "admin.members.directory.approve": "Approve",
    "admin.members.directory.view": "View",
    "admin.members.directory.delete": "Delete",
    "admin.members.details.title": "Member Details",
    "admin.members.details.address": "Address",
    "admin.members.details.paymenthistory": "Payment History",
    "admin.members.details.nopayments": "No payments yet",
    "admin.members.details.date": "Date",
    "admin.members.details.type": "Type",
    "admin.members.details.amount": "Amount",
    "admin.members.status.active": "active",
    "admin.members.status.pending": "pending",
    "admin.members.status.inactive": "inactive",
    "admin.members.approve.success": "Member approved",
    "admin.members.approve.error": "Failed to approve member",
    "admin.members.delete.confirm": "Delete Member",
    "admin.members.delete.description": "Are you sure you want to delete {name}'s membership? This action cannot be undone.",
    "admin.members.delete.cancel": "Cancel",
    "admin.members.delete.button": "Delete",
    "admin.members.delete.success": "{name}'s membership has been deleted",
    "admin.members.delete.error": "Failed to delete member",
    
    // Member Registration
    "member.registration.title": "Member Registration",
    "member.registration.description": "Complete your information to become a church member",
    "member.registration.fullname": "Full Name",
    "member.registration.email": "Email",
    "member.registration.phone": "Phone",
    "member.registration.address": "Address",
    "member.registration.children": "Number of Children",
    "member.registration.submit": "Submit Application",
    "member.registration.submitting": "Submitting...",
    "member.registration.success": "Success",
    "member.registration.successmessage": "Your membership application has been submitted for approval",
    "member.registration.successmessage.admin": "Your membership has been activated automatically",
    "member.registration.error": "Error",
    "member.registration.errormessage": "Failed to submit registration",
    "member.registration.alreadymember": "Already a Member",
    "member.registration.alreadymessage": "You already have a membership application with status: {status}",
    "member.registration.checking": "Checking membership status...",
    
    // Music - Member View
    "nav.music": "Music",
    "music.title": "Church Music",
    "music.accessDenied": "Access Denied",
    "music.accessDeniedDesc": "You must be a church member to access music resources.",
    "music.noMusicAvailable": "No music available yet",
    "music.play": "Play",
    "music.download": "Download",
    "music.lyrics": "Lyrics",
    "music.chordSheet": "Chord Sheet",
    "music.lyricsTitle": "{title} - Lyrics",
    "music.error": "Error",
    "music.allSongs": "All Songs",
    "music.song": "song",
    "music.songs2-4": "songs",
    "music.songs5+": "songs",
    "music.unknownArtist": "Unknown Artist",
    
    // Music Admin
    "admin.music.title": "Music Management",
    "admin.music.artists": "Artists",
    "admin.music.addArtist": "Add Artist",
    "admin.music.editArtist": "Edit Artist",
    "admin.music.deleteArtist": "Delete Artist",
    "admin.music.noArtists": "No artists yet",
    "admin.music.artistName": "Artist Name",
    "admin.music.artistNamePlaceholder": "Enter artist name",
    "admin.music.artistDescription": "Artist Description",
    "admin.music.artistDescriptionPlaceholder": "Enter artist description",
    "admin.music.artistImage": "Artist Image URL",
    "admin.music.selectArtist": "Select an artist",
    "admin.music.manageAlbums": "Manage Albums",
    "admin.music.hideAlbums": "Hide Albums",
    "admin.music.albumsFor": "Albums for",
    "admin.music.imageUrl": "Image URL",
    "admin.music.optional": "optional",
    "admin.music.albums": "Albums",
    "admin.music.addDisk": "Add Disk",
    "admin.music.editDisk": "Edit Disk",
    "admin.music.disks": "Disks",
    "admin.music.noDisks": "No disks yet",
    "admin.music.disk": "Disk",
    "admin.music.disksFor": "Disks for",
    "admin.music.manageDisks": "Manage Disks",
    "admin.music.hideDisks": "Hide Disks",
    "admin.music.manageSongs": "Manage Songs",
    "admin.music.hideSongs": "Hide Songs",
    "admin.music.songsForDisk": "Songs for Disk",
    "admin.music.songs": "Songs",
    "admin.music.addAlbum": "Add Album",
    "admin.music.editAlbum": "Edit Album",
    "admin.music.deleteAlbum": "Delete Album",
    "admin.music.albumTitle": "Album Title",
    "admin.music.albumDescription": "Description",
    "admin.music.albumCover": "Cover Image URL",
    "admin.music.published": "Published",
    "admin.music.unpublished": "Unpublished",
    "admin.music.save": "Save",
    "admin.music.cancel": "Cancel",
    "admin.music.saving": "Saving...",
    "admin.music.deleting": "Deleting...",
    "admin.music.delete": "Delete",
    "admin.music.noAlbums": "No albums yet",
    "admin.music.addSong": "Add Song",
    "admin.music.editSong": "Edit Song",
    "admin.music.deleteSong": "Delete Song",
    "admin.music.songTitle": "Song Title",
    "admin.music.artist": "Artist",
    "admin.music.duration": "Duration (e.g., 3:45)",
    "admin.music.album": "Album",
    "admin.music.selectAlbum": "Select an album",
    "admin.music.noAlbum": "No Album",
    "admin.music.audioFile": "Audio File",
    "admin.music.uploadAudio": "Upload Audio",
    "admin.music.uploading": "Uploading...",
    "admin.music.lyrics": "Lyrics",
    "admin.music.chordSheet": "Music Sheet (PDF)",
    "admin.music.uploadChordSheet": "Upload Music Sheet",
    "admin.music.displayOrder": "Display Order",
    "admin.music.diskNumber": "Disk Number",
    "admin.music.diskNumberPlaceholder": "e.g., 1, 2",
    "admin.music.diskName": "Disk Name",
    "admin.music.diskNamePlaceholder": "Disk Name (optional)",
    "admin.music.songNumber": "Song Number",
    "admin.music.songNumberPlaceholder": "e.g., 1, 2, 3",
    "admin.music.uploadProgress": "Upload Progress",
    "admin.music.selectAlbumFirst": "Select an album to view and manage songs",
    "admin.music.selectAlbumPlaceholder": "Choose an album...",
    "admin.music.description": "Description",
    "admin.music.songDescription": "Song Description",
    "admin.music.noSongs": "No songs yet",
    "admin.music.appleMusicLink": "Apple Music Link",
    "admin.music.songDetails": "{title} by {artist}",
    "admin.music.duration.value": "Duration: {duration}",
    "admin.music.order.value": "Order: {order}",
    "admin.music.success": "Success",
    "admin.music.albumSaved": "Album saved and auto-translated successfully",
    "admin.music.albumDeleted": "Album deleted successfully",
    "admin.music.songSaved": "Song saved and auto-translated successfully",
    "admin.music.songDeleted": "Song deleted successfully",
    "admin.music.errorSaving": "Failed to save",
    "admin.music.errorDeleting": "Failed to delete",
    "admin.music.errorUploading": "Failed to upload file",
    "admin.music.selectAlbumRequired": "Please select an album first. Create an album before adding songs.",
    "admin.music.translating": "Translating...",
    "admin.music.warning": "Warning",
  },
  ru: {
    // Header
    "nav.home": "Главная",
    "nav.about": "О нас",
    "nav.services": "Богослужения",
    "nav.ministries": "Служения",
    "nav.contact": "Контакты",
    "nav.donate": "Пожертвовать",
    "nav.resources": "Ресурсы",
    "nav.blog": "Блог",
    "nav.specialevents": "Специальные события",
    "nav.bibleschool": "Библейская школа",
    "nav.dailybiblereading": "Ежедневное чтение Библии",
    "nav.admin": "Админ",
    "nav.signin": "Войти",
    "nav.signout": "Выйти",
    "nav.profile": "Мой профиль",
    "nav.contributor": "Стать участником",
    "nav.announcements": "Объявления",
    "nav.members": "Члены",
    "nav.youtubevideos": "Видео YouTube",
    
    // YouTube Videos
    "youtube.latest.title": "Последнее видео",
    "youtube.latest.subtitle": "Смотрите наше последнее учение",
    "youtube.latest.viewall": "Посмотреть все видео",
    "youtube.admin.title": "Видео YouTube",
    "youtube.admin.subtitle": "Управление видео контентом YouTube",
    "youtube.admin.loading": "Загрузка...",
    "youtube.admin.addvideo": "Добавить видео",
    "youtube.admin.editvideo": "Редактировать видео",
    "youtube.admin.addnewvideo": "Добавить новое видео",
    "youtube.admin.form.title": "Название",
    "youtube.admin.form.url": "URL YouTube",
    "youtube.admin.form.description": "Описание",
    "youtube.admin.form.published": "Опубликовано",
    "youtube.admin.form.cancel": "Отмена",
    "youtube.admin.form.update": "Обновить",
    "youtube.admin.form.add": "Добавить",
    "youtube.admin.published": "Опубликовано",
    "youtube.admin.draft": "Черновик",
    "youtube.admin.deleteconfirm": "Вы уверены, что хотите удалить это видео?",
    "youtube.admin.updatedsuccess": "Видео успешно обновлено",
    "youtube.admin.addedsuccess": "Видео успешно добавлено",
    "youtube.admin.deletedsuccess": "Видео успешно удалено",
    "youtube.admin.saveerror": "Не удалось сохранить видео",
    "youtube.admin.deleteerror": "Не удалось удалить видео",
    "youtube.admin.novideos": "Пока нет видео. Добавьте свое первое видео, чтобы начать.",
    "youtube.page.title": "Видео YouTube",
    "youtube.page.subtitle": "Смотрите наши последние видео и учения",
    "youtube.page.loading": "Загрузка видео...",
    "youtube.page.novideos": "В настоящее время видео недоступны. Заходите позже!",
    
    // Contributor Request
    "contributor.title": "Стать участником",
    "contributor.description": "Присоединяйтесь к нашей команде преданных участников, чтобы помогать делиться верой и знаниями с нашим сообществом через статьи блога и контент библейской школы.",
    "contributor.name": "Полное имя",
    "contributor.nameplaceholder": "Введите ваше полное имя",
    "contributor.email": "Адрес электронной почты",
    "contributor.emailplaceholder": "ваш.email@example.com",
    "contributor.role": "Я хочу участвовать в",
    "contributor.roleplaceholder": "Выберите область участия",
    "contributor.blogeditor": "Статьи блога",
    "contributor.bibleschooleditor": "Контент библейской школы",
    "contributor.both": "Блог и библейская школа",
    "contributor.message": "Расскажите о себе",
    "contributor.messageplaceholder": "Поделитесь своим опытом, интересами и почему вы хотите участвовать...",
    "contributor.submit": "Отправить запрос",
    "contributor.submitting": "Отправка...",
    "contributor.success": "Запрос отправлен!",
    "contributor.successmessage": "Спасибо за ваш интерес! Мы рассмотрим ваш запрос и скоро свяжемся с вами.",
    "contributor.error": "Ошибка отправки",
    "contributor.errormessage": "Произошла ошибка при отправке вашего запроса. Пожалуйста, попробуйте еще раз.",
    "contributor.thankyou": "Спасибо!",
    "contributor.reviewmessage": "Мы получили ваш запрос на участие и скоро рассмотрим его. Вы получите ответ по электронной почте.",
    "contributor.backhome": "Вернуться на главную",
    
    // Hero
    "hero.title": "Добро пожаловать в церковь Божий Сад",
    "hero.subtitle": "Растем вместе в вере, надежде и любви",
    "hero.cta": "Присоединяйтесь к нам в воскресенье",
    
    // About
    "about.title": "О нас",
    "about.subtitle": "Сообщество, укорененное в вере и любви",
    "about.text1": "Церковь Божий Сад - это яркое, гостеприимное сообщество, где люди из всех слоев общества собираются вместе для поклонения, роста и служения. Мы верим в преобразующую силу Божьей любви и важность построения значимых отношений.",
    "about.text2": "Наша миссия - создать благоприятную среду, где каждый может открыть свою цель, развить свою веру и оказать положительное влияние в нашем сообществе и за его пределами.",
    
    // Services
    "services.title": "Расписание служений",
    "services.subtitle": "Присоединяйтесь к нам для поклонения и общения",
    "services.sunday": "Воскресное богослужение",
    "services.sunday.time": "11:00",
    "services.sunday.desc": "Современное богослужение с вдохновляющими проповедями",
    "services.bible": "Изучение Библии и молитва",
    "services.bible.time": "Вторник, 19:00",
    "services.bible.desc": "Глубокое изучение Писания и содержательное обсуждение",
    "services.prayer": "Молитвенное собрание",
    "services.prayer.time": "Пятница, 19:00",
    "services.prayer.desc": "Соберитесь вместе в молитве за нашу церковь и общество",
    "services.address": "4300 Racetrack Road, Rocklin, CA 95677",
    
    // Ministries
    "ministries.title": "Наши служения",
    "ministries.subtitle": "Откройте для себя способы расти, служить и общаться с другими",
    "ministries.children": "Детское служение",
    "ministries.children.desc": "Воспитание молодых сердец с соответствующим возрасту библейским учением и веселыми мероприятиями",
    "ministries.youth": "Молодежная группа",
    "ministries.youth.desc": "Создание прочного фундамента для подростков через общение и ученичество",
    "ministries.worship": "Команда прославления",
    "ministries.worship.desc": "Ведение собрания в искреннем прославлении и поклонении",
    "ministries.outreach": "Община",
    "ministries.outreach.desc": "Служение нашим соседям через различные программы милосердия",
    "ministries.smallgroups": "Малые группы",
    "ministries.smallgroups.desc": "Глубоко общайтесь с другими через еженедельное изучение Библии и общение",
    "ministries.women": "Женское служение",
    "ministries.women.desc": "Укрепление женщин для роста в вере через молитву, изучение и дружбу",
    "ministries.prayer": "Молитвенное служение",
    "ministries.prayer.desc": "Ходатайство за нашу церковь, общество и мир через преданную молитву",
    "ministries.missions": "Миссии",
    "ministries.missions.desc": "Поддержка глобального служения в Гватемале и Мексике, распространение Евангелия близко и далеко",
    
    // Contact
    "contact.title": "Свяжитесь с нами",
    "contact.subtitle": "Мы будем рады услышать от вас. Обращайтесь с любыми вопросами или чтобы узнать больше о нашей церкви",
    "contact.visit": "Посетите нас",
    "contact.call": "Позвоните нам",
    "contact.email": "Напишите нам",
    "contact.directions": "Маршрут",
    "contact.form.name": "Ваше имя",
    "contact.form.email": "Ваш email",
    "contact.form.phone": "Ваш телефон",
    "contact.form.message": "Ваше сообщение",
    "contact.form.submit": "Отправить сообщение",
    "contact.form.sending": "Отправка...",
    "contact.success.title": "Сообщение отправлено!",
    "contact.success.desc": "Мы свяжемся с вами как можно скорее.",
    "contact.error.title": "Ошибка",
    "contact.error.desc": "Не удалось отправить сообщение. Пожалуйста, попробуйте еще раз.",
    
    // Guatemala Mission
    "guatemala.title": "Миссия в Гватемале",
    "guatemala.subtitle": "Станьте свидетелями преобразующей силы веры и служения через нашу миссионерскую работу в Гватемале. Вместе мы строим сообщества и делимся Божьей любовью.",
    "guatemala.about.title": "О нашей миссии",
    "guatemala.about.text1": "Наша миссия в Гватемале посвящена служению нуждающимся сообществам через евангелизацию, образование, здравоохранение и развитие общин. Каждый год наша церковь отправляет миссионерские команды для работы вместе с местными церквями и организациями.",
    "guatemala.about.text2": "Через партнерство с местными сообществами мы предоставляем основные услуги, включая медицинскую помощь, образовательные программы, развитие инфраструктуры и духовное руководство. Наша миссия - продемонстрировать Божью любовь через практическое служение и долгосрочные отношения.",
    "guatemala.about.cta": "Хотите присоединиться к нашей следующей миссионерской поездке? Свяжитесь с нами, чтобы узнать больше о предстоящих возможностях.",
    "guatemala.ourwork": "Наша работа в Гватемале",
    "guatemala.medicalcare": "Медицинская помощь",
    "guatemala.medicalcare.desc": "Предоставление основных медицинских услуг, медицинских принадлежностей и санитарного просвещения недостаточно обслуживаемым сообществам по всей Гватемале.",
    "guatemala.communityoutreach": "Работа с сообществом",
    "guatemala.communityoutreach.desc": "Построение отношений и служение сообществам через образовательные программы, распределение продуктов питания и службы поддержки.",
    "guatemala.spiritualgrowth": "Духовный рост",
    "guatemala.spiritualgrowth.desc": "Партнерство с местными церквями для обеспечения духовного руководства, распространения Библии и программ ученичества.",
    "guatemala.infrastructure": "Развитие инфраструктуры",
    "guatemala.infrastructure.desc": "Строительство и ремонт домов, школ, церквей и общественных объектов для улучшения условий жизни.",
    "guatemala.gallery": "Фотогалерея",
    "guatemala.support": "Поддержите нашу миссию",
    
    // Mexico Mission
    "mexico.title": "Миссия в Мексике",
    "mexico.subtitle": "Партнерство с сообществами в Мексике, чтобы делиться Божьей любовью через служение, сострадание и Евангелие.",
    "mexico.about.title": "О нашей миссии в Мексике",
    "mexico.about.text1": "Наша миссия в Мексике сосредоточена на служении сообществам в приграничном регионе, предоставляя основные услуги, духовную поддержку и развивая долгосрочное партнерство с местными церквями и организациями.",
    "mexico.about.text2": "Мы работаем совместно с мексиканскими сообществами для решения неотложных потребностей, одновременно создавая устойчивые программы в области образования, здравоохранения и экономического развития. Наш подход подчеркивает достоинство, уважение и расширение возможностей местных лидеров.",
    "mexico.about.cta": "Хотите стать частью нашей следующей миссионерской поездки в Мексику? Свяжитесь с нами, чтобы узнать о предстоящих возможностях и как вы можете изменить ситуацию.",
    "mexico.ourwork": "Наша работа в Мексике",
    "mexico.medicalcare": "Медицинская помощь",
    "mexico.medicalcare.desc": "Предоставление медицинских клиник, стоматологических услуг и санитарного просвещения сообществам с ограниченным доступом к здравоохранению.",
    "mexico.communityoutreach": "Работа с сообществом",
    "mexico.communityoutreach.desc": "Обслуживание семей через продовольственные программы, детские мероприятия, профессиональное обучение и инициативы по развитию сообщества.",
    "mexico.spiritualgrowth": "Духовный рост",
    "mexico.spiritualgrowth.desc": "Поддержка местных церквей и служителей ресурсами, обучением и программами евангелизации для укрепления общин веры.",
    "mexico.infrastructure": "Развитие инфраструктуры",
    "mexico.infrastructure.desc": "Строительство и ремонт домов, водных систем и общественных объектов для создания устойчивых улучшений.",
    "mexico.gallery": "Фотогалерея",
    "mexico.support": "Поддержите нашу миссию",
    
    
    // Ministry Pages Common
    "ministry.getinvolved": "Присоединяйтесь",
    "ministry.contactus": "Свяжитесь с нами",
    "ministry.about": "О нас",
    "ministry.details": "Детали программы",
    "ministry.whenwemeet": "Когда мы встречаемся",
    "ministry.time": "Время",
    "ministry.agerange": "Возрастной диапазон",
    "ministry.agegroups": "Возрастные группы",
    "ministry.location": "Место",
    "ministry.activities": "Мероприятия",
    
    // Children's Ministry
    "children.about.text1": "Наше детское служение обеспечивает безопасную, любящую и увлекательную среду, где дети могут узнать об Иисусе и расти в своей вере. Через интерактивные уроки, поклонение, игры и творчество мы помогаем детям понять Божью любовь и развить личные отношения с Ним.",
    "children.about.text2": "Мы предлагаем программы, соответствующие возрасту, для младенцев и детей начальной школы, с преданными учителями, которые стремятся инвестировать в следующее поколение.",
    "children.involved": "Независимо от того, являетесь ли вы родителем, желающим записать своего ребенка, или кем-то, кто заинтересован в волонтерстве, мы будем рады услышать от вас! Свяжитесь с нами, чтобы узнать больше о нашем детском служении.",
    
    // Youth Group
    "youth.about.text1": "Наша молодежная группа - это яркое сообщество, где учащиеся средних и старших классов могут расти в своей вере, строить значимые дружеские отношения и открывать свою цель во Христе. Через увлекательные дискуссии, поклонение, игры и служебные проекты мы создаем среду, где подростки чувствуют себя желанными и ценными.",
    "youth.about.text2": "Мы рассматриваем реальные жизненные вопросы, вместе исследуем Писание и поощряем студентов развивать личные, подлинные отношения с Иисусом, которые поддержат их в подростковые годы и за их пределами.",
    "youth.whatwedo": "Чем мы занимаемся",
    "youth.involved": "Все учащиеся средних и старших классов приветствуются! Свяжитесь с нами, чтобы узнать больше о присоединении к нашей молодежной группе или возможностях волонтерства для взрослых лидеров.",
    
    // Worship Team
    "worship.about.text1": "Наша команда прославления стремится создать атмосферу, где люди могут встретиться с присутствием Бога через музыку и песни. Мы ведем поклонение каждое воскресенье, используя современные и традиционные песни, чтобы помочь нашему собранию выразить свою любовь и преданность Иисусу.",
    "worship.about.text2": "Больше чем просто исполнение музыки, мы стремимся быть лидерами поклонения, которые ведут других к более глубокой связи с Богом. Наша команда включает вокалистов, инструменталистов и технический персонал, которые работают вместе для создания значимого опыта поклонения.",
    "worship.practice": "Репетиции и богослужения",
    "worship.practice.day": "Репетиция",
    "worship.practice.days": "Четверг",
    "worship.practice.time": "19:00 - 21:00",
    "worship.service": "Служение",
    "worship.service.days": "Воскресенье",
    "worship.service.time": "11:00 богослужение",
    "worship.positions": "Позиции в команде",
    "worship.join": "Присоединяйтесь к нашей команде",
    "worship.join.text": "Если у вас есть сердце для поклонения и музыкальный талант, мы будем рады провести для вас прослушивание. Мы приветствуем музыкантов, вокалистов и технических волонтеров всех уровней. Свяжитесь с нами, чтобы назначить прослушивание или узнать больше о служении в команде прославления.",
    
    // Community Outreach
    "outreach.about.text1": "Общественное служение - это сердце нашей миссии любить и служить другим, как это делал Иисус. Мы верим, что вера демонстрируется через действие, и мы стремимся внести ощутимые изменения в жизни окружающих нас людей.",
    "outreach.about.text2": "Через различные программы и партнерства мы предоставляем практическую помощь, эмоциональную поддержку и духовное ободрение отдельным людям и семьям, нуждающимся в нашем сообществе.",
    "outreach.programs": "Наши программы",
    "outreach.involved.text": "Независимо от того, можете ли вы пожертвовать свое время, ресурсы или просто молиться за наши усилия, есть место, где вы можете изменить ситуацию. Свяжитесь с нами, чтобы узнать больше о текущих потребностях и возможностях.",
    
    // Small Groups
    "smallgroups.about.text1": "Малые группы - это сердце нашего церковного сообщества. Эти интимные собрания предоставляют пространство, где вы можете строить подлинные отношения, духовно расти и находить поддержку в жизненных взлетах и падениях. Именно здесь церковь действительно становится семьей.",
    "smallgroups.about.text2": "Каждая группа встречается еженедельно в домах по всему Роклину, чтобы изучать Слово Божье, молиться вместе, делиться жизнью и ободрять друг друга в вере. Независимо от того, новичок ли вы в церкви или давний верующий, вы найдете гостеприимное сообщество в наших малых группах.",
    "smallgroups.expect": "Чего ожидать",
    "smallgroups.where": "Где",
    "smallgroups.groupsize": "Размер группы",
    "smallgroups.format": "Формат",
    "smallgroups.available": "Доступные группы",
    "smallgroups.join": "Присоединяйтесь к малой группе",
    "smallgroups.join.text": "Готовы сделать следующий шаг в вашем духовном путешествии? Свяжитесь с нами, чтобы найти идеальную малую группу для вас. Мы поможем подобрать группу, которая соответствует вашему расписанию, жизненному этапу и интересам.",
    
    // Women's Ministry
    "women.about.text1": "Наше женское служение существует для создания безопасного, поддерживающего сообщества, где женщины всех возрастов и жизненных этапов могут общаться с Богом и друг с другом. Мы верим, что когда женщины духовно сильны и эмоционально здоровы, они оказывают влияние на свои семьи, рабочие места и сообщества мощными способами.",
    "women.about.text2": "Через изучение Библии, молитвенные собрания, специальные мероприятия и возможности служения мы поощряем женщин открывать свою идентичность во Христе, развивать свои дары и жить в своей богоданной цели.",
    "women.gatherings": "Регулярные собрания",
    "women.biblestudy": "Изучение Библии",
    "women.biblestudy.desc": "Углубленное изучение Писания с обсуждением, молитвой и кофе. Доступен присмотр за детьми.",
    "women.prayer": "Молитвенный круг",
    "women.prayer.desc": "Собрание для вознесения молитвенных просьб и ходатайства за нашу церковь, семьи и общество.",
    "women.brunch": "Ежемесячный бранч",
    "women.brunch.desc": "Непринужденное общение за бранчем с вдохновляющими спикерами или временем поклонения.",
    "women.events": "Специальные мероприятия",
    "women.connect": "Присоединяйтесь",
    "women.connect.text": "Все женщины приветствуются, независимо от того, где вы находитесь в вашем пути веры. Ищете ли вы глубокое изучение Библии, молитвенную поддержку или просто дружбу, вы найдете место здесь. Свяжитесь с нами, чтобы узнать больше или присоединиться к нашему следующему собранию.",
    
    // Prayer Ministry
    "prayer.about.text1": "Молитва - это основа всего, что мы делаем в церкви Божий Сад. Наше молитвенное служение посвящено вознесению нужд нашего собрания, сообщества и мира перед престолом Бога. Мы верим в силу молитвы преобразовывать жизни, исцелять разбитость и сдвигать горы.",
    "prayer.about.text2": "Наша молитвенная команда привержена верному ходатайству и созданию пространств, где люди могут испытать присутствие и силу Бога через молитву. Нуждаетесь ли вы в молитве или хотите присоединиться к другим в молитве, мы здесь для вас.",
    "prayer.opportunities": "Молитвенные возможности",
    "prayer.submit": "Отправить молитвенную просьбу",
    "prayer.submit.text": "Независимо от того, с чем вы сталкиваетесь, мы будем рады молиться за вас. Поделитесь своей молитвенной просьбой с нами, и наша молитвенная команда вознесет вас в молитве. Все просьбы хранятся конфиденциально, если вы не дадите разрешение поделиться с собранием.",
    "prayer.join": "Присоединяйтесь к молитвенной команде",
    "prayer.join.text": "Если у вас есть сердце для молитвы и вы хотите служить через ходатайство, мы приглашаем вас присоединиться к нашей команде молитвенного служения. Свяжитесь с нами, чтобы узнать о возможностях служения на молитвенных собраниях, в молитвенной цепи или молитвенной комнате.",
    
    // Missions Page
    "missions.title": "Миссии",
    "missions.subtitle": "Поддержка глобальной миссионерской работы, распространение Евангелия близко и далеко",
    "missions.heart.title": "Наше миссионерское сердце",
    "missions.heart.text1": "В церкви Божий Сад мы верим, что Великое Поручение призывает нас идти и делать учеников во всех народах. Наша миссионерская программа отражает эту приверженность через стратегические партнерства, краткосрочные миссионерские поездки и долгосрочную поддержку миссионеров и служений по всему миру.",
    "missions.heart.text2": "Мы сосредоточены на устойчивых миссиях, основанных на отношениях, которые расширяют возможности местных церквей и сообществ, одновременно делясь надеждой Иисуса Христа как словом, так и делом.",
    "missions.fields.title": "Наши миссионерские поля",
    "missions.guatemala.title": "Миссия в Гватемале",
    "missions.guatemala.desc": "Партнерство с сообществами для предоставления образования, здравоохранения, развития инфраструктуры и духовного руководства в сельских районах Гватемалы.",
    "missions.guatemala.cta": "Посмотреть фотогалерею →",
    "missions.mexico.title": "Миссия в Мексике",
    "missions.mexico.desc": "Служение приграничным общинам с состраданием, предоставление основных услуг и создание устойчивых программ для долгосрочного воздействия.",
    "missions.mexico.cta": "Посмотреть фотогалерею →",
    "missions.serve.title": "Как мы служим",
    "missions.serve.compassion": "Служения милосердия",
    "missions.serve.compassion.desc": "Медицинская помощь, распределение продуктов питания и помощь при стихийных бедствиях",
    "missions.serve.community": "Развитие сообщества",
    "missions.serve.community.desc": "Образование, инфраструктура и экономическое расширение возможностей",
    "missions.serve.partnerships": "Церковные партнерства",
    "missions.serve.partnerships.desc": "Поддержка местных пасторов и усилий по посадке церквей",
    "missions.serve.evangelism": "Евангелизм и ученичество",
    "missions.serve.evangelism.desc": "Распространение Евангелия и обучение лидеров",
    "missions.involved.title": "Способы участия",
    "missions.involved.trip": "Присоединяйтесь к миссионерской поездке:",
    "missions.involved.trip.desc": "Испытайте краткосрочные миссии на собственном опыте",
    "missions.involved.pray": "Молитесь:",
    "missions.involved.pray.desc": "Партнерство с нами через постоянную молитву за миссионеров и сообщества",
    "missions.involved.give": "Жертвуйте:",
    "missions.involved.give.desc": "Поддержите миссионерские проекты финансово через целевые пожертвования",
    "missions.involved.sponsor": "Спонсируйте:",
    "missions.involved.sponsor.desc": "Партнерство с миссионером или миссионерским проектом на долгосрочной основе",
    "missions.involved.text": "Призывает ли вас Бог идти, давать или молиться, для вас есть место в нашем миссионерском служении. Свяжитесь с нами, чтобы узнать о текущих возможностях и предстоящих миссионерских поездках.",
    "missions.involved.cta": "Связаться с нами",
    
    // Donate Page
    "donate.title": "Поддержите церковь Божий Сад",
    "donate.subtitle": "Ваши щедрые пожертвования помогают нашей церкви расти, обеспечивать припасы и поддержку нашей организации. Каждый вклад оказывает длительное влияние на жизни тех, кому мы служим",
    "donate.support.title": "Поддержка служения",
    "donate.support.description": "Помогите финансировать программы и служения",
    "donate.missions.title": "Глобальные миссии",
    "donate.missions.description": "Поддержите нашу работу по всему миру",
    "donate.community.title": "Община",
    "donate.community.description": "Служение нуждающимся на местном уровне",
    
    // Sermons Page
    "sermons.title": "Проповеди",
    "sermons.subtitle": "Слушайте прошлые послания и растите в вере",
    "sermons.loading": "Загрузка проповедей...",
    "sermons.nosermons": "Проповеди пока недоступны",
    "sermons.speaker": "Проповедник",
    "sermons.date": "Дата",
    "sermons.scripture": "Писание",
    "sermons.audio": "Аудио",
    "sermons.video": "Видео",
    "sermons.backToSermons": "Вернуться к проповедям",
    "sermons.description": "Описание",
    "sermons.error.title": "Ошибка",
    "sermons.error.description": "Не удалось загрузить проповеди",
    "nav.sermons": "Проповеди",
    
    // Bible Study
    "nav.biblestudy": "Изучение Библии",
    "nav.bible": "Библия",
    "biblestudy.title": "Изучение Библии",
    "biblestudy.subtitle": "Исследуйте Писание с помощью ИИ, поиска стихов и тем для изучения",
    "biblestudy.chat.title": "Задавайте вопросы о Библии",
    "biblestudy.chat.description": "Получите ответы о Писании, богословии и практическом применении",
    "biblestudy.chat.placeholder": "Задайте вопрос о Библии...",
    "biblestudy.chat.empty": "Задайте вопрос, чтобы начать изучение Библии",
    "biblestudy.chat.tryasking": "Попробуйте спросить:",
    "biblestudy.verse.title": "Поиск стихов",
    "biblestudy.verse.description": "Введите ссылку на Библию, чтобы просмотреть полный текст",
    "biblestudy.verse.placeholder": "например, Иоанна 3:16 или Псалом 23",
    "biblestudy.verse.popular": "Популярные стихи",
    "biblestudy.topics.title": "Темы для изучения",
    "biblestudy.topics.description": "Исследуйте ключевые темы в Писании",
    "biblestudy.topics.ask": "Спросите ИИ о",
    "biblestudy.tab.chat": "ИИ Вопросы",
    "biblestudy.tab.verse": "Поиск стихов",
    "biblestudy.tab.topics": "Темы для изучения",
    "biblestudy.topics.faith": "Вера и доверие",
    "biblestudy.topics.love": "Любовь и сострадание",
    "biblestudy.topics.prayer": "Молитва и поклонение",
    "biblestudy.topics.salvation": "Спасение и благодать",
    "biblestudy.topics.wisdom": "Мудрость и руководство",
    "biblestudy.topics.peace": "Мир и утешение",
    
    // Special Events
    "specialevents.title": "Особые события",
    "specialevents.description": "Отмечайте верность Бога через особые события нашей церкви и памятные моменты",
    "specialevents.gallery": "Галерея событий",
    "specialevents.noevents": "Пока нет особых событий для отображения",
    "common.loading": "Загрузка...",
    
    // Blog
    "blog.title": "Церковный блог",
    "blog.subtitle": "Размышления, учения и новости от нашего сообщества",
    "blog.loading": "Загрузка статей...",
    "blog.noPosts": "Статьи блога пока недоступны",
    "blog.searchPlaceholder": "Поиск статей блога...",
    "blog.allCategories": "Все категории",
    "blog.backToBlog": "Вернуться в блог",
    "blog.error": "Ошибка",
    "blog.errorDescription": "Не удалось загрузить статьи блога",
    "blog.postNotFound": "Статья не найдена",
    "blog.addPost": "Добавить статью",
    "blog.contribute.title": "Хотите внести свой вклад?",
    "blog.contribute.description": "Присоединяйтесь к нашей команде авторов и делитесь своими духовными мыслями, учениями и свидетельствами с нашим сообществом.",
    "blog.contribute.button": "Стать соавтором",
    
    // Daily Bible Reading
    "dailyreading.title": "Ежедневное чтение Библии",
    "dailyreading.subtitle": "Годовое путешествие через Слово Божье. Следуйте этому плану чтения, чтобы систематически прочитать Библию.",
    "dailyreading.oldtestament": "Чтение Ветхого Завета",
    "dailyreading.newtestament": "Чтение Нового Завета",
    "dailyreading.psalm": "Псалом",
    "dailyreading.proverb": "Притча",
    "dailyreading.gototoday": "Перейти к сегодня",
    "dailyreading.about.title": "О плане чтения",
    "dailyreading.about.text1": "Этот план ежедневного чтения проведет вас через всю Библию за год, с отрывками из Ветхого Завета, Нового Завета, Псалмов и Притчей каждый день.",
    "dailyreading.about.benefits": "Преимущества ежедневного чтения Библии:",
    "dailyreading.about.benefit1": "Углубляет ваши отношения с Богом",
    "dailyreading.about.benefit2": "Дает мудрость и руководство для повседневной жизни",
    "dailyreading.about.benefit3": "Укрепляет вашу веру и понимание",
    "dailyreading.about.benefit4": "Создает постоянную духовную дисциплину",
    "dailyreading.about.verse": "\"Слово Твое - светильник ноге моей и свет стезе моей.\" - Псалом 118:105",
    
    // Bible School
    "bibleschool.title": "Библейская школа",
    "bibleschool.subtitle": "Углубите понимание Писания через структурированные курсы",
    "bibleschool.welcome.title": "Добро пожаловать в Библейскую школу",
    "bibleschool.welcome.text1": "Наша Библейская школа предлагает комплексные курсы, разработанные, чтобы помочь вам расти в понимании Божьего Слова и развивать дары для служения.",
    "bibleschool.welcome.text2": "Новичок ли вы в вере или хотите углубить богословские знания, наши курсы предоставляют солидное библейское учение в поддерживающей среде.",
    "bibleschool.loading": "Загрузка курсов...",
    "bibleschool.noCourses": "В настоящее время курсы недоступны",
    "bibleschool.instructor": "Преподаватель",
    "bibleschool.duration": "Продолжительность",
    "bibleschool.schedule": "Расписание",
    "bibleschool.maxStudents": "Максимум студентов",
    "bibleschool.addCourse": "Добавить курс",
    "bibleschool.contact.title": "Интересует поступление?",
    "bibleschool.contact.text": "Если вы заинтересованы в каких-либо наших курсах, пожалуйста, свяжитесь с нами для получения дополнительной информации о поступлении, расписании и требованиях.",
    "bibleschool.contact.info": "Напишите нам или поговорите с одним из наших лидеров служения после службы.",
    "bibleschool.error": "Ошибка",
    "bibleschool.errorDescription": "Не удалось загрузить курсы",
    "bibleschool.contribute.title": "Хотите преподавать или делиться знаниями?",
    "bibleschool.contribute.description": "Делитесь своими знаниями Писания и опытом преподавания с нашими учениками. Мы ищем преданных учителей для нашей Библейской школы.",
    "bibleschool.contribute.button": "Стать преподавателем",
    
    // Admin Dashboard
    "admin.title": "Панель администратора",
    "admin.error": "Ошибка",
    "admin.success": "Успех",
    "admin.accessdenied": "Доступ запрещен. Требуется доступ администратора или наблюдателя.",
    "admin.viewermode": "Режим только для просмотра",
    "admin.tabs.donations": "Пожертвования",
    "admin.tabs.announcements": "Объявления",
    "admin.tabs.sermons": "Проповеди",
    "admin.tabs.blog": "Блог",
    "admin.tabs.bibleschool": "Библейская школа",
    "admin.tabs.bibleschool.gallery": "Галерея библейской школы",
    "admin.tabs.photos": "Фотографии миссий",
    "admin.tabs.specialevents": "Специальные события",
    "admin.specialevents.title": "Специальные события",
    "admin.specialevents.createnew": "Создать новое событие",
    "admin.specialevents.loading": "Загрузка событий...",
    "admin.specialevents.noevents": "Событий пока нет. Создайте свое первое событие!",
    "admin.specialevents.gallery": "Галерея событий",
    "admin.specialevents.selectmultiple": "Выбрать несколько",
    "admin.specialevents.cancelselection": "Отменить выбор",
    "admin.specialevents.selectall": "Выбрать все",
    "admin.specialevents.deselectall": "Снять выделение",
    "admin.specialevents.delete": "Удалить",
    "admin.specialevents.uploadmedia": "Загрузить медиа",
    "admin.specialevents.loadingmedia": "Загрузка медиа...",
    "admin.specialevents.nomedia": "Медиа пока нет. Загрузите фотографии или видео для этого события!",
    "admin.specialevents.edit": "Редактировать",
    "admin.specialevents.order": "Порядок",
    "admin.specialevents.type": "Тип",
    "admin.specialevents.editevent": "Редактировать событие",
    "admin.specialevents.createevent": "Создать новое событие",
    "admin.specialevents.updateevent": "Обновить детали события",
    "admin.specialevents.createeventdesc": "Создать новую галерею событий",
    "admin.specialevents.eventtitle": "Название события",
    "admin.specialevents.description": "Описание",
    "admin.specialevents.descriptionplaceholder": "Краткое описание события",
    "admin.specialevents.eventdate": "Дата события",
    "admin.specialevents.displayorder": "Порядок отображения",
    "admin.specialevents.cancel": "Отмена",
    "admin.specialevents.update": "Обновить событие",
    "admin.specialevents.create": "Создать событие",
    "admin.specialevents.editmedia": "Редактировать медиа",
    "admin.specialevents.uploadmediatitle": "Загрузить медиа",
    "admin.specialevents.updatemedia": "Обновить детали медиа",
    "admin.specialevents.uploadmediadesc": "Загрузите фотографии или видео для этого события. Поддерживаемые форматы: JPG, PNG, WebP, MP4, MOV, AVI, WebM",
    "admin.specialevents.youtubeurl": "URL YouTube видео (необязательно)",
    "admin.specialevents.youtubeurlplaceholder": "https://www.youtube.com/watch?v=... или https://youtu.be/...",
    "admin.specialevents.youtubehelp": "Если вы укажете URL YouTube, вы можете дополнительно загрузить пользовательскую миниатюру ниже.",
    "admin.specialevents.thumbnailimage": "Миниатюра (необязательно)",
    "admin.specialevents.selectfiles": "Выберите файлы (изображения и видео)",
    "admin.specialevents.customthumbnail": "Загрузите пользовательскую миниатюру для вашего YouTube видео (необязательно)",
    "admin.specialevents.filesselected": "файл(ов) выбрано:",
    "admin.specialevents.uploading": "Загрузка:",
    "admin.specialevents.mediatitle": "Название",
    "admin.specialevents.mediatitlerequired": "Название обязательно для каждого элемента",
    "admin.specialevents.mediadescription": "Описание (необязательно)",
    "admin.specialevents.mediadescriptionplaceholder": "Добавьте описание для этого медиа",
    "admin.specialevents.mediaorder": "Порядок отображения",
    "admin.specialevents.savemedia": "Сохранить медиа",
    "admin.specialevents.date": "Дата",
    "admin.tabs.users": "Пользователи",
    "admin.tabs.members": "Участники",
    "admin.tabs.contributors": "Запросы участников",
    "admin.tabs.content": "Контент сайта",
    "admin.donations.totalreceived": "Всего получено",
    "admin.donations.netdonations": "Чистые пожертвования",
    "admin.donations.processingfees": "Комиссии за обработку",
    "admin.donations.includingfees": "Включая комиссии за обработку",
    "admin.donations.excludingfees": "Исключая комиссии за обработку",
    "admin.donations.stripefees": "Комиссии PayPal",
    "admin.donations.filtercategory": "Фильтр по категории:",
    "admin.donations.allcategories": "Все категории",
    "admin.donations.recent": "Недавние пожертвования",
    "admin.donations.loading": "Загрузка пожертвований...",
    "admin.donations.nodonations": "Пожертвования не найдены.",
    "admin.donations.date": "Дата",
    "admin.donations.donor": "Донор",
    "admin.donations.email": "Email",
    "admin.donations.category": "Категория",
    "admin.donations.donation": "Пожертвование",
    "admin.donations.processingfee": "Комиссия за обработку",
    "admin.donations.total": "Всего",
    "admin.donations.actions": "Действия",
    "admin.category.guatemala": "Миссия в Гватемале",
    "admin.category.mexico": "Миссия в Мексике",
    
    "admin.category.church": "Пожертвование церкви",
    "admin.category.tithe": "Десятина",
    "admin.users.title": "Управление пользователями",
    "admin.users.loading": "Загрузка пользователей...",
    "admin.users.nousers": "Пользователи не найдены.",
    "admin.users.email": "Email",
    "admin.users.role": "Роль",
    "admin.users.roles": "Роли и управление",
    "admin.users.created": "Создан",
    "admin.users.lastsignin": "Последний вход",
    "admin.users.never": "Никогда",
    "admin.users.actions": "Действия",
    "admin.users.admin": "Администратор",
    "admin.users.viewer": "Наблюдатель",
    "admin.users.primary": "Основной",
    "admin.users.user": "Пользователь",
    "admin.users.norole": "Роль не назначена",
    "admin.users.deleteconfirm": "Вы уверены, что хотите удалить пользователя",
    "admin.users.cannotdelete": "Невозможно удалить основного администратора",
    "admin.users.deleteuser": "Удалить пользователя",
    "admin.users.yourself": "Вы (не можете удалить себя)",
    "admin.users.makeadmin": "Сделать администратором",
    "admin.users.makeviewer": "Сделать наблюдателем",
    "admin.users.removeadmin": "Удалить администратора",
    "admin.users.removeviewer": "Удалить наблюдателя",
    "admin.users.roleassigned": "Роль успешно назначена",
    "admin.users.roleremoved": "Роль успешно удалена",
    "admin.users.roleerror": "Не удалось обновить роль",
    "admin.users.createuser": "Создать пользователя",
    "admin.users.createnew": "Создать нового пользователя",
    "admin.users.createdescription": "Добавьте нового пользователя в систему и при необходимости назначьте ему роль",
    "admin.users.password": "Пароль",
    "admin.users.passwordhint": "Минимум 6 символов",
    "admin.users.assignrole": "Назначить роль (необязательно)",
    "admin.users.cancel": "Отмена",
    "admin.users.create": "Создать",
    "admin.users.createsuccess": "Пользователь успешно создан",
    "admin.users.createerror": "Не удалось создать пользователя",
    
    // Admin Contributor Requests
    "admin.contributors.title": "Запросы участников",
    "admin.contributors.loading": "Загрузка запросов...",
    "admin.contributors.norequests": "Запросы участников не найдены",
    "admin.contributors.name": "Имя",
    "admin.contributors.email": "Email",
    "admin.contributors.role": "Запрошенная роль",
    "admin.contributors.message": "Сообщение",
    "admin.contributors.status": "Статус",
    "admin.contributors.created": "Отправлено",
    "admin.contributors.actions": "Действия",
    "admin.contributors.field.name": "Имя",
    "admin.contributors.field.email": "Email",
    "admin.contributors.field.role": "Запрошенная роль",
    "admin.contributors.field.message": "Сообщение",
    "admin.contributors.field.status": "Статус",
    "admin.contributors.field.date": "Дата",
    "admin.contributors.status.all": "Все",
    "admin.contributors.status.pending": "В ожидании",
    "admin.contributors.status.approved": "Одобрено",
    "admin.contributors.status.rejected": "Отклонено",
    "admin.contributors.roles.blogeditor": "Редактор блога",
    "admin.contributors.roles.bibleschooleditor": "Редактор библейской школы",
    "admin.contributors.roles.both": "Оба (Блог + Библейская школа)",
    "admin.contributors.pending": "В ожидании",
    "admin.contributors.approved": "Одобрено и приглашено успешно",
    "admin.contributors.rejected": "Отклонено успешно",
    "admin.contributors.approve": "Одобрить и создать пользователя",
    "admin.contributors.reject": "Отклонить",
    "admin.contributors.delete": "Удалить",
    "admin.contributors.approvesuccess": "Запрос одобрен и пользователь приглашен",
    "admin.contributors.approveerror": "Не удалось одобрить запрос",
    "admin.contributors.rejectsuccess": "Запрос отклонен",
    "admin.contributors.rejecterror": "Не удалось отклонить запрос",
    "admin.contributors.deletesuccess": "Запрос удален",
    "admin.contributors.deleteerror": "Не удалось удалить запрос",
    "admin.contributors.deleteconfirm": "Вы уверены, что хотите удалить этот запрос?",
    "admin.contributors.rejectconfirm": "Вы уверены, что хотите отклонить этот запрос?",
    "admin.contributors.nomessage": "Сообщение не предоставлено",
    
    "admin.sermons.title": "Управление проповедями",
    "admin.sermons.addsermon": "Добавить проповедь",
    "admin.sermons.editsermon": "Редактировать проповедь",
    "admin.sermons.addnew": "Добавить новую проповедь",
    "admin.sermons.filldetails": "Заполните данные проповеди ниже",
    "admin.sermons.title.label": "Название",
    "admin.sermons.speaker.label": "Проповедник",
    "admin.sermons.date.label": "Дата",
    "admin.sermons.scripture.label": "Ссылка на Писание",
    "admin.sermons.scripture.placeholder": "например, Иоанна 3:16",
    "admin.sermons.description.label": "Описание",
    "admin.sermons.audiourl.label": "Ссылка на аудио",
    "admin.sermons.audiourl.placeholder": "https://example.com/audio.mp3",
    "admin.sermons.videourl.label": "Ссылка на видео",
    "admin.sermons.videourl.placeholder": "https://youtube.com/watch?v=...",
    "admin.sermons.cancel": "Отмена",
    "admin.sermons.create": "Создать",
    "admin.sermons.update": "Обновить",
    "admin.sermons.loading": "Загрузка проповедей...",
    "admin.sermons.nosermons": "Проповедей пока нет. Нажмите \"Добавить проповедь\", чтобы создать.",
    "admin.sermons.deleteconfirm": "Вы уверены, что хотите удалить эту проповедь?",
    "admin.sermons.media": "Медиа",
    "admin.sermons.mediatype.audio": "Аудио",
    "admin.sermons.mediatype.video": "Видео",
    "admin.sermons.translations": "Управление переводами",
    "admin.sermons.translations.title": "Переводы проповеди",
    "admin.sermons.translations.description": "Добавьте переводы для этой проповеди на разных языках",
    "admin.sermons.translations.language": "Язык",
    "admin.sermons.translations.english": "Английский",
    "admin.sermons.translations.russian": "Русский",
    "admin.sermons.translations.spanish": "Испанский",
    "admin.sermons.translations.save": "Сохранить переводы",
    "admin.sermons.translations.success": "Переводы успешно сохранены",
    "admin.sermons.translations.error": "Не удалось сохранить переводы",
    "admin.sermons.updatedsuccess": "Проповедь успешно обновлена.",
    "admin.sermons.addedsuccess": "Проповедь успешно добавлена.",
    "admin.sermons.deletedsuccess": "Проповедь успешно удалена.",
    "admin.sermons.saveerror": "Не удалось сохранить проповедь.",
    "admin.sermons.fetcherror": "Не удалось загрузить проповеди.",
    "admin.sermons.deleteerror": "Не удалось удалить проповедь.",
    "admin.sermons.published": "Опубликовано",
    
    // Admin - Announcements
    "admin.announcements.title": "Управление объявлениями",
    "admin.announcements.add": "Добавить объявление",
    "admin.announcements.edit": "Редактировать объявление",
    "admin.announcements.editdesc": "Обновите данные объявления ниже.",
    "admin.announcements.adddesc": "Заполните данные для нового объявления.",
    "admin.announcements.titlefield": "Заголовок",
    "admin.announcements.titleplaceholder": "Заголовок объявления",
    "admin.announcements.content": "Содержание",
    "admin.announcements.contentplaceholder": "Содержание объявления",
    "admin.announcements.published": "Опубликовать объявление",
    "admin.announcements.create": "Создать",
    "admin.announcements.update": "Обновить",
    "admin.announcements.cancel": "Отмена",
    "admin.announcements.loading": "Загрузка объявлений...",
    "admin.announcements.noannouncements": "Объявлений пока нет. Создайте первое объявление.",
    "admin.announcements.deleteconfirm": "Вы уверены, что хотите удалить это объявление?",
    "admin.announcements.translations": "Управление переводами",
    "admin.announcements.translationsdesc": "Переведите \"{title}\" на другие языки",
    "admin.announcements.translations.russian": "Русский (RU)",
    "admin.announcements.translations.spanish": "Испанский (ES)",
    "admin.announcements.translations.title": "Заголовок",
    "admin.announcements.translations.content": "Содержание",
    "admin.announcements.translations.save": "Сохранить переводы",
    "admin.announcements.translations.cancel": "Отмена",
    "admin.announcements.status.published": "Опубликовано",
    "admin.announcements.status.draft": "Черновик",
    "admin.announcements.updatedsuccess": "Объявление успешно обновлено",
    "admin.announcements.addedsuccess": "Объявление успешно создано",
    "admin.announcements.deletedsuccess": "Объявление успешно удалено",
    "admin.announcements.saveerror": "Не удалось сохранить объявление",
    "admin.announcements.fetcherror": "Не удалось загрузить объявления",
    "admin.announcements.deleteerror": "Не удалось удалить объявление",
    "admin.announcements.translationssuccess": "Переводы успешно сохранены",
    "admin.announcements.translationserror": "Не удалось сохранить переводы",
    
    "admin.users.deletedsuccess": "Пользователь успешно удален.",
    "admin.users.deleteerror": "Не удалось удалить пользователя.",
    "admin.users.fetcherror": "Не удалось загрузить пользователей.",
    "admin.donations.deletedsuccess": "Пожертвование успешно удалено.",
    "admin.donations.deleteerror": "Не удалось удалить пожертвование.",
    "admin.donations.fetcherror": "Не удалось загрузить пожертвования.",
    "admin.photos.title": "Фотографии миссий",
    "admin.photos.upload": "Загрузить фото",
    "admin.photos.uploadnew": "Загрузить новое фото",
    "admin.photos.edit": "Редактировать фото",
    "admin.photos.description": "Добавить фото в галереи миссий",
    "admin.photos.file": "Файл фото",
    "admin.photos.phototitle": "Название фото",
    "admin.photos.photodescription": "Описание фото",
    "admin.photos.displayorder": "Порядок отображения",
    "admin.photos.order": "Порядок",
    "admin.photos.cancel": "Отмена",
    "admin.photos.uploadbutton": "Загрузить",
    "admin.photos.update": "Обновить",
    "admin.photos.editbutton": "Редактировать",
    "admin.photos.deletebutton": "Удалить",
    "admin.photos.loading": "Загрузка фото...",
    "admin.photos.nophotos": "Фотографии не найдены.",
    "admin.photos.fetcherror": "Не удалось загрузить фотографии.",
    "admin.photos.uploadsuccess": "Фото успешно загружено.",
    "admin.photos.uploaderror": "Не удалось загрузить фото.",
    "admin.photos.updatesuccess": "Фото успешно обновлено.",
    "admin.photos.updateerror": "Не удалось обновить фото.",
    "admin.photos.deletesuccess": "Фото успешно удалено.",
    "admin.photos.deleteerror": "Не удалось удалить фото.",
    "admin.photos.deleteconfirm": "Вы уверены, что хотите удалить это фото?",
    "admin.photos.nofile": "Пожалуйста, выберите файл для загрузки.",
    
    // Admin Bible School
    "admin.bibleschool.title": "Управление библейской школой",
    "admin.bibleschool.add": "Добавить курс",
    "admin.bibleschool.create": "Создать курс",
    "admin.bibleschool.edit": "Редактировать курс",
    "admin.bibleschool.update": "Обновить курс",
    "admin.bibleschool.cancel": "Отмена",
    "admin.bibleschool.loading": "Загрузка курсов...",
    "admin.bibleschool.nocourses": "Курсы не найдены.",
    "admin.bibleschool.filter.all": "Все",
    "admin.bibleschool.filter.published": "Опубликовано",
    "admin.bibleschool.filter.draft": "Черновик",
    "admin.bibleschool.field.title": "Название",
    "admin.bibleschool.field.description": "Описание",
    "admin.bibleschool.field.instructor": "Преподаватель",
    "admin.bibleschool.field.duration": "Продолжительность",
    "admin.bibleschool.field.duration.placeholder": "напр., 8 недель, 3 месяца",
    "admin.bibleschool.field.level": "Уровень",
    "admin.bibleschool.field.level.placeholder": "напр., Начальный, Средний, Продвинутый",
    "admin.bibleschool.field.schedule": "Расписание",
    "admin.bibleschool.field.schedule.placeholder": "напр., Понедельники 19:00",
    "admin.bibleschool.field.maxstudents": "Макс. Студентов",
    "admin.bibleschool.field.image": "Изображение",
    "admin.bibleschool.field.published": "Опубликовано",
    "admin.bibleschool.status": "Статус",
    "admin.bibleschool.actions": "Действия",
    "admin.bibleschool.translations": "Переводы",
    "admin.bibleschool.status.published": "Опубликовано",
    "admin.bibleschool.status.draft": "Черновик",
    
    // Admin Blog
    "admin.blog.title": "Управление блогом",
    "admin.blog.add": "Добавить статью",
    "admin.blog.create": "Создать статью",
    "admin.blog.edit": "Редактировать статью",
    "admin.blog.update": "Обновить статью",
    "admin.blog.cancel": "Отмена",
    "admin.blog.loading": "Загрузка статей...",
    "admin.blog.noposts": "Статьи не найдены.",
    "admin.blog.filter.all": "Все",
    "admin.blog.filter.published": "Опубликовано",
    "admin.blog.filter.draft": "Черновик",
    "admin.blog.field.title": "Название",
    "admin.blog.field.slug": "URL",
    "admin.blog.field.slug.placeholder": "генерируется-из-названия",
    "admin.blog.field.author": "Автор",
    "admin.blog.field.category": "Категория",
    "admin.blog.field.excerpt": "Краткое описание",
    "admin.blog.field.content": "Содержание",
    "admin.blog.field.image": "URL изображения",
    "admin.blog.field.published": "Опубликовано",
    "admin.blog.status": "Статус",
    "admin.blog.actions": "Действия",
    "admin.blog.translations": "Переводы",
    "admin.blog.translations.title": "Переводы статьи",
    "admin.blog.translations.desc": "Добавьте переводы для этой статьи на разных языках",
    "admin.blog.translations.save": "Сохранить переводы",
    "admin.blog.status.published": "Опубликовано",
    "admin.blog.status.draft": "Черновик",
    
    // Membership
    "membership.title": "Членство в церкви",
    "membership.subtitle": "Присоединяйтесь к нашей церковной семье и станьте активной частью нашего сообщества",
    "membership.benefit1.title": "Общение",
    "membership.benefit1.description": "Общайтесь с другими членами и стройте прочные отношения",
    "membership.benefit2.title": "Активное участие",
    "membership.benefit2.description": "Участвуйте в церковных мероприятиях и служениях",
    "membership.benefit3.title": "Удобные пожертвования",
    "membership.benefit3.description": "Простое онлайн отслеживание десятин и взносов",
    "membership.notloggedin": "Войдите или создайте аккаунт, чтобы начать заявку на членство",
    "membership.signinsignup": "Войти / Регистрация",
    "membership.gotoportal": "Перейти в портал участника",
    "membership.readytojoin": "Готовы присоединиться к нашей церковной семье?",
    "membership.becomemember": "Стать членом",
    "membership.whattoexpect": "Что ожидать",
    "membership.step1": "Заполните простую регистрационную форму",
    "membership.step2": "Предоставьте основную контактную информацию",
    "membership.step3": "Ваша заявка будет рассмотрена администрацией церкви",
    "membership.step4": "После одобрения вы получите доступ к порталу участников",
    "membership.step5": "Делайте онлайн-платежи десятин и взносов",
    "membership.step6": "Общайтесь с другими членами церкви",
    
    // Member Portal
    "member.portal.title": "Портал участника",
    "member.portal.welcome": "Добро пожаловать!",
    "member.portal.needregistration": "Вам необходимо завершить регистрацию участника, чтобы получить доступ к порталу.",
    "member.portal.completeregistration": "Завершить регистрацию",
    "member.portal.tabs.profile": "Профиль",
    "member.portal.tabs.payments": "Платежи",
    "member.portal.tabs.family": "Семья",
    "member.portal.tabs.security": "Безопасность",
    
    // Member Profile
    "member.profile.title": "Профиль участника",
    "member.profile.description": "Просмотр и обновление вашей информации",
    "member.profile.edit": "Редактировать профиль",
    "member.profile.fullName": "Полное имя",
    "member.profile.email": "Электронная почта",
    "member.profile.phone": "Телефон",
    "member.profile.address": "Адрес",
    "member.profile.numberOfKids": "Количество детей",
    "member.profile.notProvided": "Не указано",
    "member.profile.memberSince": "Участник с",
    "member.profile.saveChanges": "Сохранить изменения",
    "member.profile.saving": "Сохранение...",
    "member.profile.cancel": "Отмена",
    "member.profile.success": "Успех",
    "member.profile.updateSuccess": "Профиль успешно обновлен",
    "member.profile.error": "Ошибка",
    "member.profile.updateError": "Не удалось обновить профиль",
    
    // Member Payments
    "member.payments.makePayment": "Сделать платеж",
    "member.payments.submitPayment": "Внести десятину или ежемесячные взносы",
    "member.payments.tithe": "Десятина",
    "member.payments.titheDescription": "Внести вашу десятину",
    "member.payments.payTithe": "Внести десятину",
    "member.payments.monthlyDues": "Ежемесячные взносы",
    "member.payments.monthlyRentalDues": "Ежемесячная арендная плата",
    "member.payments.duesDescription": "Оплатить ежемесячные взносы участника",
    "member.payments.payDues": "Оплатить взносы",
    "member.payments.payMonthlyRentalDues": "Оплатить ежемесячную аренду",
    "member.payments.duesSchedule": "График ежемесячных взносов",
    "member.payments.monthlyPaymentSchedule": "График ежемесячных платежей",
    "member.payments.scheduleAmount": "$30 за каждого члена церкви",
    "member.payments.history": "История платежей",
    "member.payments.historyDescription": "Просмотреть все ваши прошлые платежи",
    "member.payments.loading": "Загрузка...",
    "member.payments.noHistory": "История платежей пока отсутствует",
    "member.payments.date": "Дата",
    "member.payments.type": "Тип",
    "member.payments.amount": "Сумма",
    "member.payments.status": "Статус",
    "member.payments.error": "Ошибка",
    "member.payments.loadError": "Не удалось загрузить историю платежей",
    
    // MFA
    "mfa.title": "Двухфакторная аутентификация",
    "mfa.enable": "Включить двухфакторную аутентификацию",
    "mfa.disable": "Отключить двухфакторную аутентификацию",
    "mfa.setupDescription": "Добавьте дополнительный уровень безопасности в свой аккаунт",
    "mfa.setupExplanation": "Двухфакторная аутентификация (2FA) добавляет дополнительный уровень безопасности, требуя код подтверждения с вашего телефона при входе.",
    "mfa.authenticatorRequired": "Вам понадобится приложение для аутентификации, такое как Google Authenticator, Authy или Microsoft Authenticator.",
    "mfa.setupButton": "Настроить двухфакторную аутентификацию",
    "mfa.protectedDescription": "Ваш аккаунт защищен с помощью 2FA",
    "mfa.enabledMessage": "Двухфакторная аутентификация включена. Вам понадобится приложение для аутентификации при входе.",
    
    // Family Management
    "member.family.title": "Семейные связи",
    "member.family.description": "Управление вашими семейными связями",
    "member.family.addRelationship": "Добавить связь",
    "member.family.addTitle": "Добавить семейную связь",
    "member.family.addDescription": "Связаться с другим членом церкви",
    "member.family.churchMember": "Член церкви",
    "member.family.selectMember": "Выберите участника",
    "member.family.relationshipType": "Тип связи",
    "member.family.selectRelationship": "Выберите связь",
    "member.family.spouse": "Супруг(а)",
    "member.family.parent": "Родитель",
    "member.family.child": "Ребенок",
    "member.family.sibling": "Брат/Сестра",
    "member.family.addButton": "Добавить связь",
    "member.family.loading": "Загрузка...",
    "member.family.noRelationships": "Семейные связи еще не добавлены",
    "member.family.name": "Имя",
    "member.family.relationship": "Связь",
    "member.family.actions": "Действия",
    "member.family.success": "Успех",
    "member.family.addSuccess": "Семейная связь добавлена",
    "member.family.removeSuccess": "Связь удалена",
    "member.family.error": "Ошибка",
    "member.family.loadError": "Не удалось загрузить семейные связи",
    "member.family.selectError": "Пожалуйста, выберите участника и тип связи",
    "member.family.addError": "Не удалось добавить связь",
    "member.family.removeError": "Не удалось удалить связь",
    
    // Admin Member Management
    "admin.members.title": "Управление участниками",
    "admin.members.directory.title": "Справочник участников",
    "admin.members.directory.description": "Просмотр и управление всеми членами церкви",
    "admin.members.directory.loading": "Загрузка...",
    "admin.members.directory.name": "Имя",
    "admin.members.directory.email": "Email",
    "admin.members.directory.phone": "Телефон",
    "admin.members.directory.status": "Статус",
    "admin.members.directory.children": "Дети",
    "admin.members.directory.actions": "Действия",
    "admin.members.directory.na": "Н/Д",
    "admin.members.directory.approve": "Утвердить",
    "admin.members.directory.view": "Просмотр",
    "admin.members.directory.delete": "Удалить",
    "admin.members.details.title": "Детали участника",
    "admin.members.details.address": "Адрес",
    "admin.members.details.paymenthistory": "История платежей",
    "admin.members.details.nopayments": "Платежей пока нет",
    "admin.members.details.date": "Дата",
    "admin.members.details.type": "Тип",
    "admin.members.details.amount": "Сумма",
    "admin.members.status.active": "активен",
    "admin.members.status.pending": "ожидает",
    "admin.members.status.inactive": "неактивен",
    "admin.members.approve.success": "Участник утвержден",
    "admin.members.approve.error": "Не удалось утвердить участника",
    "admin.members.delete.confirm": "Удалить участника",
    "admin.members.delete.description": "Вы уверены, что хотите удалить членство {name}? Это действие нельзя отменить.",
    "admin.members.delete.cancel": "Отмена",
    "admin.members.delete.button": "Удалить",
    "admin.members.delete.success": "Членство {name} удалено",
    "admin.members.delete.error": "Не удалось удалить участника",
    
    // Member Registration
    "member.registration.title": "Регистрация участника",
    "member.registration.description": "Заполните информацию, чтобы стать членом церкви",
    "member.registration.fullname": "Полное имя",
    "member.registration.email": "Email",
    "member.registration.phone": "Телефон",
    "member.registration.address": "Адрес",
    "member.registration.children": "Количество детей",
    "member.registration.submit": "Отправить заявку",
    "member.registration.submitting": "Отправка...",
    "member.registration.success": "Успешно",
    "member.registration.successmessage": "Ваша заявка на членство отправлена на рассмотрение",
    "member.registration.successmessage.admin": "Ваше членство активировано автоматически",
    "member.registration.error": "Ошибка",
    "member.registration.errormessage": "Не удалось отправить регистрацию",
    "member.registration.alreadymember": "Уже участник",
    "member.registration.alreadymessage": "У вас уже есть заявка на членство со статусом: {status}",
    "member.registration.checking": "Проверка статуса членства...",
    
    // Music - Member View
    "nav.music": "Музыка",
    "music.title": "Церковная музыка",
    "music.accessDenied": "Доступ запрещен",
    "music.accessDeniedDesc": "Вы должны быть членом церкви для доступа к музыкальным ресурсам.",
    "music.noMusicAvailable": "Музыка пока недоступна",
    "music.play": "Воспроизвести",
    "music.download": "Скачать",
    "music.lyrics": "Текст песни",
    "music.chordSheet": "Аккорды",
    "music.lyricsTitle": "{title} - Текст песни",
    "music.error": "Ошибка",
    "music.allSongs": "Все песни",
    "music.song": "песня",
    "music.songs2-4": "песни",
    "music.songs5+": "песен",
    "music.unknownArtist": "Неизвестный исполнитель",
    
    // Music Admin
    "admin.music.title": "Управление музыкой",
    "admin.music.artists": "Исполнители",
    "admin.music.addArtist": "Добавить исполнителя",
    "admin.music.editArtist": "Редактировать исполнителя",
    "admin.music.deleteArtist": "Удалить исполнителя",
    "admin.music.noArtists": "Исполнителей пока нет",
    "admin.music.artistName": "Имя исполнителя",
    "admin.music.artistNamePlaceholder": "Введите имя исполнителя",
    "admin.music.artistDescription": "Описание исполнителя",
    "admin.music.artistDescriptionPlaceholder": "Введите описание исполнителя",
    "admin.music.artistImage": "URL изображения исполнителя",
    "admin.music.selectArtist": "Выберите исполнителя",
    "admin.music.manageAlbums": "Управление альбомами",
    "admin.music.hideAlbums": "Скрыть альбомы",
    "admin.music.albumsFor": "Альбомы для",
    "admin.music.imageUrl": "URL изображения",
    "admin.music.optional": "необязательно",
    "admin.music.albums": "Альбомы",
    "admin.music.addDisk": "Добавить диск",
    "admin.music.editDisk": "Редактировать диск",
    "admin.music.disks": "Диски",
    "admin.music.noDisks": "Дисков пока нет",
    "admin.music.disk": "Диск",
    "admin.music.disksFor": "Диски для",
    "admin.music.manageDisks": "Управление дисками",
    "admin.music.hideDisks": "Скрыть диски",
    "admin.music.manageSongs": "Управление песнями",
    "admin.music.hideSongs": "Скрыть песни",
    "admin.music.songsForDisk": "Песни диска",
    "admin.music.songs": "Песни",
    "admin.music.addAlbum": "Добавить альбом",
    "admin.music.editAlbum": "Редактировать альбом",
    "admin.music.deleteAlbum": "Удалить альбом",
    "admin.music.albumTitle": "Название альбома",
    "admin.music.albumDescription": "Описание",
    "admin.music.albumCover": "URL обложки альбома",
    "admin.music.published": "Опубликовано",
    "admin.music.unpublished": "Не опубликовано",
    "admin.music.save": "Сохранить",
    "admin.music.cancel": "Отмена",
    "admin.music.saving": "Сохранение...",
    "admin.music.deleting": "Удаление...",
    "admin.music.delete": "Удалить",
    "admin.music.noAlbums": "Альбомов пока нет",
    "admin.music.addSong": "Добавить песню",
    "admin.music.editSong": "Редактировать песню",
    "admin.music.deleteSong": "Удалить песню",
    "admin.music.songTitle": "Название песни",
    "admin.music.artist": "Исполнитель",
    "admin.music.duration": "Длительность (например, 3:45)",
    "admin.music.album": "Альбом",
    "admin.music.selectAlbum": "Выберите альбом",
    "admin.music.noAlbum": "Без альбома",
    "admin.music.audioFile": "Аудиофайл",
    "admin.music.uploadAudio": "Загрузить аудио",
    "admin.music.uploading": "Загрузка...",
    "admin.music.lyrics": "Текст песни",
    "admin.music.chordSheet": "Ноты (PDF)",
    "admin.music.uploadChordSheet": "Загрузить ноты",
    "admin.music.displayOrder": "Порядок отображения",
    "admin.music.diskNumber": "Номер диска",
    "admin.music.diskNumberPlaceholder": "например, 1, 2",
    "admin.music.diskName": "Название диска",
    "admin.music.diskNamePlaceholder": "Название диска (необязательно)",
    "admin.music.songNumber": "Номер песни",
    "admin.music.songNumberPlaceholder": "например, 1, 2, 3",
    "admin.music.uploadProgress": "Прогресс загрузки",
    "admin.music.selectAlbumFirst": "Выберите альбом для просмотра и управления песнями",
    "admin.music.selectAlbumPlaceholder": "Выберите альбом...",
    "admin.music.description": "Описание",
    "admin.music.songDescription": "Описание песни",
    "admin.music.noSongs": "Песен пока нет",
    "admin.music.appleMusicLink": "Ссылка Apple Music",
    "admin.music.songDetails": "{title} - {artist}",
    "admin.music.duration.value": "Длительность: {duration}",
    "admin.music.order.value": "Порядок: {order}",
    "admin.music.success": "Успешно",
    "admin.music.albumSaved": "Альбом успешно сохранен и автоматически переведен",
    "admin.music.albumDeleted": "Альбом успешно удален",
    "admin.music.songSaved": "Песня успешно сохранена и автоматически переведена",
    "admin.music.songDeleted": "Песня успешно удалена",
    "admin.music.errorSaving": "Не удалось сохранить",
    "admin.music.errorDeleting": "Не удалось удалить",
    "admin.music.errorUploading": "Не удалось загрузить файл",
    "admin.music.selectAlbumRequired": "Сначала выберите альбом. Создайте альбом перед добавлением песен.",
    "admin.music.translating": "Перевод...",
    "admin.music.warning": "Предупреждение",
  },
  es: {
    // Header
    "nav.home": "Inicio",
    "nav.about": "Acerca de",
    "nav.services": "Servicios",
    "nav.ministries": "Ministerios",
    "nav.contact": "Contáctanos",
    "nav.donate": "Donar",
    "nav.resources": "Recursos",
    "nav.blog": "Blog",
    "nav.specialevents": "Eventos Especiales",
    "nav.bibleschool": "Escuela Bíblica",
    "nav.dailybiblereading": "Lectura Diaria de la Biblia",
    "nav.admin": "Admin",
    "nav.signin": "Iniciar Sesión",
    "nav.signout": "Cerrar Sesión",
    "nav.profile": "Mi Perfil",
    "nav.contributor": "Convertirse en Colaborador",
    "nav.announcements": "Anuncios",
    "nav.members": "Miembros",
    "nav.youtubevideos": "Videos de YouTube",
    
    // YouTube Videos
    "youtube.latest.title": "Último Video",
    "youtube.latest.subtitle": "Mira nuestra enseñanza más reciente",
    "youtube.latest.viewall": "Ver Todos los Videos",
    "youtube.admin.title": "Videos de YouTube",
    "youtube.admin.subtitle": "Administrar contenido de videos de YouTube",
    "youtube.admin.loading": "Cargando...",
    "youtube.admin.addvideo": "Agregar Video",
    "youtube.admin.editvideo": "Editar Video",
    "youtube.admin.addnewvideo": "Agregar Nuevo Video",
    "youtube.admin.form.title": "Título",
    "youtube.admin.form.url": "URL de YouTube",
    "youtube.admin.form.description": "Descripción",
    "youtube.admin.form.published": "Publicado",
    "youtube.admin.form.cancel": "Cancelar",
    "youtube.admin.form.update": "Actualizar",
    "youtube.admin.form.add": "Agregar",
    "youtube.admin.published": "Publicado",
    "youtube.admin.draft": "Borrador",
    "youtube.admin.deleteconfirm": "¿Estás seguro de que quieres eliminar este video?",
    "youtube.admin.updatedsuccess": "Video actualizado exitosamente",
    "youtube.admin.addedsuccess": "Video agregado exitosamente",
    "youtube.admin.deletedsuccess": "Video eliminado exitosamente",
    "youtube.admin.saveerror": "Error al guardar el video",
    "youtube.admin.deleteerror": "Error al eliminar el video",
    "youtube.admin.novideos": "Aún no hay videos. Agrega tu primer video para comenzar.",
    "youtube.page.title": "Videos de YouTube",
    "youtube.page.subtitle": "Mira nuestros últimos videos y enseñanzas",
    "youtube.page.loading": "Cargando videos...",
    "youtube.page.novideos": "No hay videos disponibles en este momento. ¡Vuelve pronto!",
    
    // Contributor Request
    "contributor.title": "Convertirse en Colaborador",
    "contributor.description": "Únete a nuestro equipo de colaboradores dedicados para ayudar a compartir la fe y el conocimiento con nuestra comunidad a través de publicaciones de blog y contenido de la escuela bíblica.",
    "contributor.name": "Nombre Completo",
    "contributor.nameplaceholder": "Ingresa tu nombre completo",
    "contributor.email": "Correo Electrónico",
    "contributor.emailplaceholder": "tu.correo@ejemplo.com",
    "contributor.role": "Me interesa contribuir a",
    "contributor.roleplaceholder": "Selecciona el área de contribución",
    "contributor.blogeditor": "Publicaciones del Blog",
    "contributor.bibleschooleditor": "Contenido de la Escuela Bíblica",
    "contributor.both": "Blog y Escuela Bíblica",
    "contributor.message": "Cuéntanos sobre ti",
    "contributor.messageplaceholder": "Comparte tu experiencia, intereses y por qué te gustaría contribuir...",
    "contributor.submit": "Enviar Solicitud",
    "contributor.submitting": "Enviando...",
    "contributor.success": "¡Solicitud Enviada!",
    "contributor.successmessage": "¡Gracias por tu interés! Revisaremos tu solicitud y nos pondremos en contacto pronto.",
    "contributor.error": "Envío Fallido",
    "contributor.errormessage": "Hubo un error al enviar tu solicitud. Por favor, inténtalo de nuevo.",
    "contributor.thankyou": "¡Gracias!",
    "contributor.reviewmessage": "Hemos recibido tu solicitud de colaborador y la revisaremos pronto. Te contactaremos por correo electrónico.",
    "contributor.backhome": "Volver al Inicio",
    
    // Hero
    "hero.title": "Bienvenido a la Iglesia Jardín de Dios",
    "hero.subtitle": "Creciendo juntos en fe, esperanza y amor",
    "hero.cta": "Únete a Nosotros Este Domingo",
    
    // About
    "about.title": "Acerca de Nosotros",
    "about.subtitle": "Una comunidad arraigada en fe y amor",
    "about.text1": "La Iglesia Jardín de Dios es una comunidad vibrante y acogedora donde personas de todos los ámbitos de la vida se reúnen para adorar, crecer y servir. Creemos en el poder transformador del amor de Dios y la importancia de construir relaciones significativas.",
    "about.text2": "Nuestra misión es crear un ambiente nutritivo donde todos puedan descubrir su propósito, desarrollar su fe y hacer un impacto positivo en nuestra comunidad y más allá.",
    
    // Services
    "services.title": "Horarios de Servicio",
    "services.subtitle": "Únete a nosotros para adoración y compañerismo",
    "services.sunday": "Culto Dominical",
    "services.sunday.time": "11:00 AM",
    "services.sunday.desc": "Servicio de adoración contemporánea con mensajes inspiradores",
    "services.bible": "Estudio Bíblico y Oración",
    "services.bible.time": "Martes, 7:00 PM",
    "services.bible.desc": "Estudio profundo de las Escrituras y discusión significativa",
    "services.prayer": "Reunión de Oración",
    "services.prayer.time": "Viernes, 7:00 PM",
    "services.prayer.desc": "Reunámonos en oración por nuestra iglesia y comunidad",
    "services.address": "4300 Racetrack Road, Rocklin, CA 95677",
    
    // Ministries
    "ministries.title": "Nuestros Ministerios",
    "ministries.subtitle": "Descubre formas de crecer, servir y conectarte con otros",
    "ministries.children": "Ministerio Infantil",
    "ministries.children.desc": "Nutriendo corazones jóvenes con enseñanza bíblica apropiada para su edad y actividades divertidas",
    "ministries.youth": "Grupo Juvenil",
    "ministries.youth.desc": "Construyendo bases sólidas para adolescentes a través del compañerismo y discipulado",
    "ministries.worship": "Equipo de Alabanza",
    "ministries.worship.desc": "Dirigiendo a la congregación en alabanza y adoración sincera",
    "ministries.outreach": "Alcance Comunitario",
    "ministries.outreach.desc": "Sirviendo a nuestros vecinos a través de varios programas de compasión",
    "ministries.smallgroups": "Grupos Pequeños",
    "ministries.smallgroups.desc": "Conecta profundamente con otros a través del estudio bíblico semanal y compañerismo",
    "ministries.women": "Ministerio de Mujeres",
    "ministries.women.desc": "Empoderando a las mujeres para crecer en fe a través de la oración, estudio y amistad",
    "ministries.prayer": "Ministerio de Oración",
    "ministries.prayer.desc": "Intercediendo por nuestra iglesia, comunidad y mundo a través de oración dedicada",
    "ministries.missions": "Misiones",
    "ministries.missions.desc": "Apoyando el alcance global en Guatemala y México, compartiendo el Evangelio cerca y lejos",
    
    // Contact
    "contact.title": "Ponte en Contacto",
    "contact.subtitle": "Nos encantaría saber de ti. Comunícate con cualquier pregunta o para aprender más sobre nuestra iglesia",
    "contact.visit": "Visítanos",
    "contact.call": "Llámanos",
    "contact.email": "Envíanos un Email",
    "contact.directions": "Obtener Direcciones",
    "contact.form.name": "Tu Nombre",
    "contact.form.email": "Tu Email",
    "contact.form.phone": "Tu Teléfono",
    "contact.form.message": "Tu Mensaje",
    "contact.form.submit": "Enviar Mensaje",
    "contact.form.sending": "Enviando...",
    "contact.success.title": "¡Mensaje Enviado!",
    "contact.success.desc": "Te responderemos lo antes posible.",
    "contact.error.title": "Error",
    "contact.error.desc": "No se pudo enviar el mensaje. Por favor, inténtalo de nuevo.",
    
    // Guatemala Mission
    "guatemala.title": "Misión en Guatemala",
    "guatemala.subtitle": "Presencia el poder transformador de la fe y el servicio a través de nuestro trabajo misionero en Guatemala. Juntos, estamos construyendo comunidades y compartiendo el amor de Dios.",
    "guatemala.about.title": "Acerca de Nuestra Misión",
    "guatemala.about.text1": "Nuestra Misión en Guatemala se dedica a servir a comunidades necesitadas a través del alcance basado en la fe, educación, atención médica y desarrollo comunitario. Cada año, nuestra iglesia envía equipos misioneros para trabajar junto con iglesias y organizaciones locales.",
    "guatemala.about.text2": "A través de asociaciones con comunidades locales, proporcionamos servicios esenciales que incluyen atención médica, programas educativos, desarrollo de infraestructura y orientación espiritual. Nuestra misión es demostrar el amor de Dios a través del servicio práctico y relaciones duraderas.",
    "guatemala.about.cta": "¿Interesado en unirte a nuestro próximo viaje misionero? Contáctanos para aprender más sobre las próximas oportunidades.",
    "guatemala.ourwork": "Nuestro Trabajo en Guatemala",
    "guatemala.medicalcare": "Atención Médica",
    "guatemala.medicalcare.desc": "Proporcionando servicios de atención médica esenciales, suministros médicos y educación en salud a comunidades desatendidas en toda Guatemala.",
    "guatemala.communityoutreach": "Alcance Comunitario",
    "guatemala.communityoutreach.desc": "Construyendo relaciones y sirviendo a comunidades a través de programas educativos, distribución de alimentos y servicios de apoyo.",
    "guatemala.spiritualgrowth": "Crecimiento Espiritual",
    "guatemala.spiritualgrowth.desc": "Asociándonos con iglesias locales para proporcionar orientación espiritual, distribución de Biblias y programas de discipulado.",
    "guatemala.infrastructure": "Desarrollo de Infraestructura",
    "guatemala.infrastructure.desc": "Construyendo y renovando hogares, escuelas, iglesias e instalaciones comunitarias para mejorar las condiciones de vida.",
    "guatemala.gallery": "Galería de Fotos",
    "guatemala.support": "Apoya Nuestra Misión",
    
    // Mexico Mission
    "mexico.title": "Misión en México",
    "mexico.subtitle": "Asociándonos con comunidades en México para compartir el amor de Dios a través del servicio, compasión y el Evangelio.",
    "mexico.about.title": "Acerca de Nuestra Misión en México",
    "mexico.about.text1": "Nuestra Misión en México se enfoca en servir a comunidades en la región fronteriza, proporcionando servicios esenciales, apoyo espiritual y desarrollando asociaciones a largo plazo con iglesias y organizaciones locales.",
    "mexico.about.text2": "Trabajamos colaborativamente con comunidades mexicanas para abordar necesidades inmediatas mientras construimos programas sostenibles para educación, atención médica y desarrollo económico. Nuestro enfoque enfatiza la dignidad, el respeto y el empoderamiento de líderes locales.",
    "mexico.about.cta": "¿Quieres ser parte de nuestro próximo viaje misionero a México? Contáctanos para conocer las próximas oportunidades y cómo puedes hacer una diferencia.",
    "mexico.ourwork": "Nuestro Trabajo en México",
    "mexico.medicalcare": "Atención Médica",
    "mexico.medicalcare.desc": "Ofreciendo clínicas médicas, servicios dentales y educación en salud a comunidades con acceso limitado a la atención médica.",
    "mexico.communityoutreach": "Alcance Comunitario",
    "mexico.communityoutreach.desc": "Sirviendo a familias a través de programas de alimentos, actividades para niños, capacitación vocacional e iniciativas de desarrollo comunitario.",
    "mexico.spiritualgrowth": "Crecimiento Espiritual",
    "mexico.spiritualgrowth.desc": "Apoyando a iglesias y ministros locales con recursos, capacitación y programas de evangelización para fortalecer comunidades de fe.",
    "mexico.infrastructure": "Desarrollo de Infraestructura",
    "mexico.infrastructure.desc": "Construyendo y reparando hogares, sistemas de agua e instalaciones comunitarias para crear mejoras sostenibles.",
    "mexico.gallery": "Galería de Fotos",
    "mexico.support": "Apoya Nuestra Misión",
    
    
    // Ministry Pages Common
    "ministry.getinvolved": "Involúcrate",
    "ministry.contactus": "Contáctanos",
    "ministry.about": "Acerca de",
    "ministry.details": "Detalles del Programa",
    "ministry.whenwemeet": "Cuándo Nos Reunimos",
    "ministry.time": "Hora",
    "ministry.agerange": "Rango de Edad",
    "ministry.agegroups": "Grupos de Edad",
    "ministry.location": "Ubicación",
    "ministry.activities": "Actividades",
    
    // Children's Ministry
    "children.about.text1": "Nuestro Ministerio Infantil proporciona un ambiente seguro, amoroso y atractivo donde los niños pueden aprender acerca de Jesús y crecer en su fe. A través de lecciones interactivas, adoración, juegos y manualidades, ayudamos a los niños a entender el amor de Dios y desarrollar una relación personal con Él.",
    "children.about.text2": "Ofrecemos programas apropiados para la edad desde bebés hasta la escuela primaria, con maestros dedicados que están apasionados por invertir en la próxima generación.",
    "children.involved": "Ya seas un padre que busca inscribir a su hijo o alguien interesado en ser voluntario, ¡nos encantaría saber de ti! Contáctanos para aprender más sobre nuestro Ministerio Infantil.",
    
    // Youth Group
    "youth.about.text1": "Nuestro Grupo Juvenil es una comunidad vibrante donde los estudiantes de secundaria pueden crecer en su fe, construir amistades significativas y descubrir su propósito en Cristo. A través de discusiones atractivas, adoración, juegos y proyectos de servicio, creamos un ambiente donde los adolescentes se sienten bienvenidos y valorados.",
    "youth.about.text2": "Abordamos problemas de la vida real, exploramos las Escrituras juntos y alentamos a los estudiantes a desarrollar una relación personal y auténtica con Jesús que los sostendrá durante sus años adolescentes y más allá.",
    "youth.whatwedo": "Lo Que Hacemos",
    "youth.involved": "¡Todos los estudiantes de secundaria son bienvenidos! Contáctanos para aprender más sobre unirte a nuestro grupo juvenil u oportunidades de voluntariado para líderes adultos.",
    
    // Worship Team
    "worship.about.text1": "Nuestro Equipo de Alabanza es apasionado por crear una atmósfera donde las personas puedan encontrarse con la presencia de Dios a través de la música y el canto. Dirigimos la adoración cada domingo, usando canciones contemporáneas y tradicionales para ayudar a nuestra congregación a expresar su amor y devoción a Jesús.",
    "worship.about.text2": "Más que solo tocar música, nos esforzamos por ser líderes de adoración que guían a otros a una conexión más profunda con Dios. Nuestro equipo incluye vocalistas, instrumentistas y miembros del equipo técnico que trabajan juntos para crear experiencias significativas de adoración.",
    "worship.practice": "Prácticas y Horarios de Servicio",
    "worship.practice.day": "Práctica",
    "worship.practice.days": "Jueves",
    "worship.practice.time": "7:00 PM - 9:00 PM",
    "worship.service": "Servicio",
    "worship.service.days": "Domingos",
    "worship.service.time": "11:00 AM Servicio de Adoración",
    "worship.positions": "Posiciones del Equipo",
    "worship.join": "Únete a Nuestro Equipo",
    "worship.join.text": "Si tienes un corazón para la adoración y talento musical, nos encantaría que hagas una audición para nuestro equipo. Damos la bienvenida a músicos, vocalistas y voluntarios técnicos de todos los niveles. Contáctanos para programar una audición o aprender más sobre servir en el equipo de alabanza.",
    
    // Community Outreach
    "outreach.about.text1": "El Alcance Comunitario está en el corazón de nuestra misión de amar y servir a otros como lo hizo Jesús. Creemos que la fe se demuestra a través de la acción, y estamos comprometidos a hacer una diferencia tangible en las vidas de quienes nos rodean.",
    "outreach.about.text2": "A través de varios programas y asociaciones, proporcionamos ayuda práctica, apoyo emocional y aliento espiritual a individuos y familias necesitadas en toda nuestra comunidad.",
    "outreach.programs": "Nuestros Programas",
    "outreach.involved.text": "Ya sea que puedas ofrecer tu tiempo como voluntario, donar recursos o simplemente orar por nuestros esfuerzos, hay un lugar para que hagas una diferencia. Contáctanos para aprender más sobre las necesidades actuales y oportunidades.",
    
    // Small Groups
    "smallgroups.about.text1": "Los Grupos Pequeños son el corazón de nuestra comunidad eclesial. Estas reuniones íntimas proporcionan un espacio donde puedes construir relaciones auténticas, crecer espiritualmente y encontrar apoyo a través de los altibajos de la vida. Es donde la iglesia verdaderamente se convierte en familia.",
    "smallgroups.about.text2": "Cada grupo se reúne semanalmente en hogares en toda el área de Rocklin para estudiar la Palabra de Dios, orar juntos, compartir la vida y alentarse mutuamente en la fe. Ya seas nuevo en la iglesia o un creyente de mucho tiempo, encontrarás una comunidad acogedora en nuestros grupos pequeños.",
    "smallgroups.expect": "Qué Esperar",
    "smallgroups.where": "Dónde",
    "smallgroups.groupsize": "Tamaño del Grupo",
    "smallgroups.format": "Formato",
    "smallgroups.available": "Grupos Disponibles",
    "smallgroups.join": "Únete a un Grupo Pequeño",
    "smallgroups.join.text": "¿Listo para dar el siguiente paso en tu viaje espiritual? Contáctanos para encontrar el grupo pequeño perfecto para ti. Te ayudaremos a encontrar un grupo que se ajuste a tu horario, etapa de vida e intereses.",
    
    // Women's Ministry
    "women.about.text1": "Nuestro Ministerio de Mujeres existe para crear una comunidad segura y de apoyo donde mujeres de todas las edades y etapas de la vida puedan conectarse con Dios y entre sí. Creemos que cuando las mujeres son espiritualmente fuertes y emocionalmente saludables, impactan a sus familias, lugares de trabajo y comunidades de maneras poderosas.",
    "women.about.text2": "A través de estudios bíblicos, reuniones de oración, eventos especiales y oportunidades de servicio, alentamos a las mujeres a descubrir su identidad en Cristo, desarrollar sus dones y vivir su propósito dado por Dios.",
    "women.gatherings": "Reuniones Regulares",
    "women.biblestudy": "Estudio Bíblico",
    "women.biblestudy.desc": "Estudio profundo de las Escrituras con discusión, oración y café. Cuidado infantil disponible.",
    "women.prayer": "Círculo de Oración",
    "women.prayer.desc": "Reunión para elevar peticiones de oración e interceder por nuestra iglesia, familias y comunidad.",
    "women.brunch": "Brunch Mensual",
    "women.brunch.desc": "Compañerismo casual durante el brunch con oradores inspiradores o tiempo de adoración.",
    "women.events": "Eventos Especiales",
    "women.connect": "Conéctate",
    "women.connect.text": "Todas las mujeres son bienvenidas, sin importar dónde estén en su viaje de fe. Ya sea que busques un estudio bíblico profundo, apoyo en oración o simplemente amistad, encontrarás un lugar aquí. Contáctanos para aprender más o unirte a nuestra próxima reunión.",
    
    // Prayer Ministry
    "prayer.about.text1": "La oración es el fundamento de todo lo que hacemos en la Iglesia Jardín de Dios. Nuestro Ministerio de Oración está dedicado a elevar las necesidades de nuestra congregación, comunidad y mundo ante el trono de Dios. Creemos en el poder de la oración para transformar vidas, sanar corazones rotos y mover montañas.",
    "prayer.about.text2": "Nuestro equipo de oración está comprometido con la intercesión fiel y la creación de espacios donde las personas puedan experimentar la presencia y el poder de Dios a través de la oración. Ya sea que necesites oración o quieras unirte a otros en orar, estamos aquí para ti.",
    "prayer.opportunities": "Oportunidades de Oración",
    "prayer.submit": "Enviar una Petición de Oración",
    "prayer.submit.text": "No importa lo que estés enfrentando, estaríamos honrados de orar por ti. Comparte tu petición de oración con nosotros, y nuestro equipo de oración te elevará en oración. Todas las peticiones se mantienen confidenciales a menos que des permiso para compartir con la congregación.",
    "prayer.join": "Únete al Equipo de Oración",
    "prayer.join.text": "Si tienes un corazón para la oración y quieres servir a través de la intercesión, te invitamos a unirte a nuestro equipo del Ministerio de Oración. Contáctanos para aprender sobre oportunidades para servir en reuniones de oración, la cadena de oración o ministerio de sala de oración.",
    
    // Missions Page
    "missions.title": "Misiones",
    "missions.subtitle": "Apoyando el alcance global y el trabajo misionero, compartiendo el Evangelio cerca y lejos",
    "missions.heart.title": "Nuestro Corazón Misionero",
    "missions.heart.text1": "En la Iglesia Jardín de Dios, creemos que la Gran Comisión nos llama a ir y hacer discípulos de todas las naciones. Nuestro programa de misiones refleja este compromiso a través de asociaciones estratégicas, viajes misioneros a corto plazo y apoyo a largo plazo de misioneros y ministerios en todo el mundo.",
    "missions.heart.text2": "Nos enfocamos en misiones sostenibles y basadas en relaciones que empoderan a las iglesias y comunidades locales mientras compartimos la esperanza de Jesucristo tanto en palabra como en hechos.",
    "missions.fields.title": "Nuestros Campos Misioneros",
    "missions.guatemala.title": "Misión en Guatemala",
    "missions.guatemala.desc": "Asociándonos con comunidades para proporcionar educación, atención médica, desarrollo de infraestructura y orientación espiritual en toda la Guatemala rural.",
    "missions.guatemala.cta": "Ver Galería de Fotos →",
    "missions.mexico.title": "Misión en México",
    "missions.mexico.desc": "Sirviendo a comunidades fronterizas con compasión, proporcionando servicios esenciales y construyendo programas sostenibles para un impacto a largo plazo.",
    "missions.mexico.cta": "Ver Galería de Fotos →",
    "missions.serve.title": "Cómo Servimos",
    "missions.serve.compassion": "Ministerios de Compasión",
    "missions.serve.compassion.desc": "Atención médica, distribución de alimentos y ayuda en desastres",
    "missions.serve.community": "Desarrollo Comunitario",
    "missions.serve.community.desc": "Educación, infraestructura y empoderamiento económico",
    "missions.serve.partnerships": "Asociaciones Eclesiales",
    "missions.serve.partnerships.desc": "Apoyando a pastores locales y esfuerzos de plantación de iglesias",
    "missions.serve.evangelism": "Evangelismo y Discipulado",
    "missions.serve.evangelism.desc": "Compartiendo el Evangelio y capacitando líderes",
    "missions.involved.title": "Formas de Involucrarse",
    "missions.involved.trip": "Únete a un Viaje Misionero:",
    "missions.involved.trip.desc": "Experimenta misiones a corto plazo de primera mano",
    "missions.involved.pray": "Ora:",
    "missions.involved.pray.desc": "Asóciate con nosotros a través de la oración constante por los misioneros y comunidades",
    "missions.involved.give": "Da:",
    "missions.involved.give.desc": "Apoya proyectos misioneros financieramente a través de donaciones designadas",
    "missions.involved.sponsor": "Patrocina:",
    "missions.involved.sponsor.desc": "Asóciate con un misionero o proyecto misionero a largo plazo",
    "missions.involved.text": "Ya sea que Dios te esté llamando a ir, dar u orar, hay un lugar para ti en nuestro ministerio de misiones. Contáctanos para conocer las oportunidades actuales y los próximos viajes misioneros.",
    "missions.involved.cta": "Contáctanos",
    
    // Donate Page
    "donate.title": "Apoya a la Iglesia Jardín de Dios",
    "donate.subtitle": "Tus generosas donaciones ayudan a nuestra iglesia a crecer, proporcionar suministros y apoyo a nuestra organización. Cada contribución tiene un impacto duradero en las vidas de aquellos a quienes servimos",
    "donate.support.title": "Apoyo al Ministerio",
    "donate.support.description": "Ayuda a financiar programas y ministerios",
    "donate.missions.title": "Misiones Globales",
    "donate.missions.description": "Apoya nuestro trabajo alrededor del mundo",
    "donate.community.title": "Alcance Comunitario",
    "donate.community.description": "Sirve a los necesitados localmente",
    
    // Sermons Page
    "sermons.title": "Sermones",
    "sermons.subtitle": "Escucha mensajes pasados y crece en la fe",
    "sermons.loading": "Cargando sermones...",
    "sermons.nosermons": "Aún no hay sermones disponibles",
    "sermons.speaker": "Predicador",
    "sermons.date": "Fecha",
    "sermons.scripture": "Escritura",
    "sermons.audio": "Audio",
    "sermons.video": "Video",
    "sermons.backToSermons": "Volver a Sermones",
    "sermons.description": "Descripción",
    "sermons.error.title": "Error",
    "sermons.error.description": "No se pudieron cargar los sermones",
    "nav.sermons": "Sermones",
    
    // Bible Study
    "nav.biblestudy": "Estudio Bíblico",
    "nav.bible": "Biblia",
    "biblestudy.title": "Estudio Bíblico",
    "biblestudy.subtitle": "Explore las Escrituras con asistencia de IA, búsqueda de versículos y temas de estudio",
    "biblestudy.chat.title": "Haga preguntas bíblicas",
    "biblestudy.chat.description": "Obtenga respuestas sobre las Escrituras, teología y aplicación práctica",
    "biblestudy.chat.placeholder": "Haga una pregunta bíblica...",
    "biblestudy.chat.empty": "Haga una pregunta para comenzar su estudio bíblico",
    "biblestudy.chat.tryasking": "Intente preguntar:",
    "biblestudy.verse.title": "Búsqueda de versículos",
    "biblestudy.verse.description": "Ingrese una referencia bíblica para ver el texto completo",
    "biblestudy.verse.placeholder": "ej., Juan 3:16 o Salmo 23",
    "biblestudy.verse.popular": "Versículos populares",
    "biblestudy.topics.title": "Temas de estudio",
    "biblestudy.topics.description": "Explore temas clave en las Escrituras",
    "biblestudy.topics.ask": "Pregunte a la IA sobre",
    "biblestudy.tab.chat": "Preguntas con IA",
    "biblestudy.tab.verse": "Búsqueda de versículos",
    "biblestudy.tab.topics": "Temas de estudio",
    "biblestudy.topics.faith": "Fe y confianza",
    "biblestudy.topics.love": "Amor y compasión",
    "biblestudy.topics.prayer": "Oración y adoración",
    "biblestudy.topics.salvation": "Salvación y gracia",
    "biblestudy.topics.wisdom": "Sabiduría y guía",
    "biblestudy.topics.peace": "Paz y consuelo",
    
    // Special Events
    "specialevents.title": "Eventos Especiales",
    "specialevents.description": "Celebra la fidelidad de Dios a través de los eventos especiales de nuestra iglesia y momentos memorables",
    "specialevents.gallery": "Galería del Evento",
    "specialevents.noevents": "Aún no hay eventos especiales para mostrar",
    "common.loading": "Cargando...",
    
    // Blog
    "blog.title": "Blog de la Iglesia",
    "blog.subtitle": "Devocionales, enseñanzas y actualizaciones de nuestra comunidad",
    "blog.loading": "Cargando publicaciones...",
    "blog.noPosts": "Aún no hay publicaciones de blog disponibles",
    "blog.searchPlaceholder": "Buscar publicaciones del blog...",
    "blog.allCategories": "Todas las Categorías",
    "blog.backToBlog": "Volver al Blog",
    "blog.error": "Error",
    "blog.errorDescription": "No se pudieron cargar las publicaciones del blog",
    "blog.postNotFound": "Publicación no encontrada",
    "blog.addPost": "Agregar Publicación",
    "blog.contribute.title": "¿Quieres contribuir?",
    "blog.contribute.description": "Únete a nuestro equipo de escritores y comparte tus pensamientos espirituales, enseñanzas y testimonios con nuestra comunidad.",
    "blog.contribute.button": "Convertirse en Colaborador",
    
    // Daily Bible Reading
    "dailyreading.title": "Lectura Diaria de la Biblia",
    "dailyreading.subtitle": "Un viaje de un año a través de la Palabra de Dios. Sigue este plan de lectura para leer toda la Biblia de manera sistemática.",
    "dailyreading.oldtestament": "Lectura del Antiguo Testamento",
    "dailyreading.newtestament": "Lectura del Nuevo Testamento",
    "dailyreading.psalm": "Salmo",
    "dailyreading.proverb": "Proverbio",
    "dailyreading.gototoday": "Ir a hoy",
    "dailyreading.about.title": "Acerca de este Plan de Lectura",
    "dailyreading.about.text1": "Este plan de lectura diaria te guía a través de toda la Biblia en un año, con selecciones del Antiguo Testamento, Nuevo Testamento, Salmos y Proverbios cada día.",
    "dailyreading.about.benefits": "Beneficios de la Lectura Diaria de la Biblia:",
    "dailyreading.about.benefit1": "Profundiza tu relación con Dios",
    "dailyreading.about.benefit2": "Proporciona sabiduría y guía para la vida diaria",
    "dailyreading.about.benefit3": "Fortalece tu fe y comprensión",
    "dailyreading.about.benefit4": "Crea una disciplina espiritual constante",
    "dailyreading.about.verse": "\"Lámpara es a mis pies tu palabra, y lumbrera a mi camino.\" - Salmos 119:105",
    
    // Bible School
    "bibleschool.title": "Escuela Bíblica",
    "bibleschool.subtitle": "Profundiza tu comprensión de las Escrituras a través de cursos estructurados",
    "bibleschool.welcome.title": "Bienvenido a la Escuela Bíblica",
    "bibleschool.welcome.text1": "Nuestra Escuela Bíblica ofrece cursos completos diseñados para ayudarte a crecer en tu comprensión de la Palabra de Dios y desarrollar tus dones para el ministerio.",
    "bibleschool.welcome.text2": "Ya seas nuevo en la fe o busques profundizar tu conocimiento teológico, nuestros cursos proporcionan enseñanza bíblica sólida en un ambiente de apoyo.",
    "bibleschool.loading": "Cargando cursos...",
    "bibleschool.noCourses": "No hay cursos disponibles en este momento",
    "bibleschool.instructor": "Instructor",
    "bibleschool.duration": "Duración",
    "bibleschool.schedule": "Horario",
    "bibleschool.maxStudents": "Máximo de Estudiantes",
    "bibleschool.addCourse": "Agregar Curso",
    "bibleschool.contact.title": "¿Interesado en Inscribirte?",
    "bibleschool.contact.text": "Si estás interesado en alguno de nuestros cursos, contáctanos para más información sobre inscripción, horarios y requisitos.",
    "bibleschool.contact.info": "Envíanos un correo o habla con uno de nuestros líderes ministeriales después del servicio.",
    "bibleschool.error": "Error",
    "bibleschool.errorDescription": "No se pudieron cargar los cursos",
    "bibleschool.contribute.title": "¿Quieres enseñar o compartir conocimientos?",
    "bibleschool.contribute.description": "Comparte tu conocimiento de las Escrituras y experiencia docente con nuestros estudiantes. Buscamos maestros dedicados para nuestra Escuela Bíblica.",
    "bibleschool.contribute.button": "Convertirse en Instructor",
    
    // Admin Dashboard
    "admin.title": "Panel de Administración",
    "admin.error": "Error",
    "admin.success": "Éxito",
    "admin.accessdenied": "Acceso denegado. Se requiere acceso de administrador o visor.",
    "admin.viewermode": "Modo de Solo Vista",
    "admin.tabs.donations": "Donaciones",
    "admin.tabs.announcements": "Anuncios",
    "admin.tabs.sermons": "Sermones",
    "admin.tabs.blog": "Blog",
    "admin.tabs.bibleschool": "Escuela Bíblica",
    "admin.tabs.bibleschool.gallery": "Galería de la Escuela Bíblica",
    "admin.tabs.photos": "Fotos de Misiones",
    "admin.tabs.specialevents": "Eventos Especiales",
    "admin.specialevents.title": "Eventos Especiales",
    "admin.specialevents.createnew": "Crear Nuevo Evento",
    "admin.specialevents.loading": "Cargando eventos...",
    "admin.specialevents.noevents": "Aún no hay eventos. ¡Crea tu primer evento!",
    "admin.specialevents.gallery": "Galería de Eventos",
    "admin.specialevents.selectmultiple": "Seleccionar Múltiples",
    "admin.specialevents.cancelselection": "Cancelar Selección",
    "admin.specialevents.selectall": "Seleccionar Todo",
    "admin.specialevents.deselectall": "Deseleccionar Todo",
    "admin.specialevents.delete": "Eliminar",
    "admin.specialevents.uploadmedia": "Subir Medios",
    "admin.specialevents.loadingmedia": "Cargando medios...",
    "admin.specialevents.nomedia": "Aún no hay medios. ¡Sube fotos o videos a este evento!",
    "admin.specialevents.edit": "Editar",
    "admin.specialevents.order": "Orden",
    "admin.specialevents.type": "Tipo",
    "admin.specialevents.editevent": "Editar Evento",
    "admin.specialevents.createevent": "Crear Nuevo Evento",
    "admin.specialevents.updateevent": "Actualizar detalles del evento",
    "admin.specialevents.createeventdesc": "Crear una nueva galería de eventos",
    "admin.specialevents.eventtitle": "Título del Evento",
    "admin.specialevents.description": "Descripción",
    "admin.specialevents.descriptionplaceholder": "Breve descripción del evento",
    "admin.specialevents.eventdate": "Fecha del Evento",
    "admin.specialevents.displayorder": "Orden de Visualización",
    "admin.specialevents.cancel": "Cancelar",
    "admin.specialevents.update": "Actualizar Evento",
    "admin.specialevents.create": "Crear Evento",
    "admin.specialevents.editmedia": "Editar Medios",
    "admin.specialevents.uploadmediatitle": "Subir Medios",
    "admin.specialevents.updatemedia": "Actualizar detalles de medios",
    "admin.specialevents.uploadmediadesc": "Sube fotos o videos a este evento. Formatos compatibles: JPG, PNG, WebP, MP4, MOV, AVI, WebM",
    "admin.specialevents.youtubeurl": "URL de Video de YouTube (Opcional)",
    "admin.specialevents.youtubeurlplaceholder": "https://www.youtube.com/watch?v=... o https://youtu.be/...",
    "admin.specialevents.youtubehelp": "Si proporcionas una URL de YouTube, puedes opcionalmente subir una miniatura personalizada a continuación.",
    "admin.specialevents.thumbnailimage": "Imagen en Miniatura (Opcional)",
    "admin.specialevents.selectfiles": "Seleccionar Archivos (Imágenes y Videos)",
    "admin.specialevents.customthumbnail": "Sube una miniatura personalizada para tu video de YouTube (opcional)",
    "admin.specialevents.filesselected": "archivo(s) seleccionado(s):",
    "admin.specialevents.uploading": "Subiendo:",
    "admin.specialevents.mediatitle": "Título",
    "admin.specialevents.mediatitlerequired": "El título es requerido para cada elemento",
    "admin.specialevents.mediadescription": "Descripción (Opcional)",
    "admin.specialevents.mediadescriptionplaceholder": "Agrega una descripción para este medio",
    "admin.specialevents.mediaorder": "Orden de Visualización",
    "admin.specialevents.savemedia": "Guardar Medios",
    "admin.specialevents.date": "Fecha",
    "admin.tabs.users": "Usuarios",
    "admin.tabs.members": "Miembros",
    "admin.tabs.contributors": "Solicitudes de Colaboradores",
    "admin.tabs.content": "Contenido del Sitio",
    "admin.donations.totalreceived": "Total Recibido",
    "admin.donations.netdonations": "Donaciones Netas",
    "admin.donations.processingfees": "Tarifas de Procesamiento",
    "admin.donations.includingfees": "Incluyendo tarifas de procesamiento",
    "admin.donations.excludingfees": "Excluyendo tarifas de procesamiento",
    "admin.donations.stripefees": "Tarifas de procesamiento de PayPal",
    "admin.donations.filtercategory": "Filtrar por Categoría:",
    "admin.donations.allcategories": "Todas las Categorías",
    "admin.donations.recent": "Donaciones Recientes",
    "admin.donations.loading": "Cargando donaciones...",
    "admin.donations.nodonations": "No se encontraron donaciones.",
    "admin.donations.date": "Fecha",
    "admin.donations.donor": "Donante",
    "admin.donations.email": "Email",
    "admin.donations.category": "Categoría",
    "admin.donations.donation": "Donación",
    "admin.donations.processingfee": "Tarifa de Procesamiento",
    "admin.donations.total": "Total",
    "admin.donations.actions": "Acciones",
    "admin.category.guatemala": "Misión en Guatemala",
    "admin.category.mexico": "Misión en México",
    
    "admin.category.church": "Donación a la Iglesia",
    "admin.category.tithe": "Diezmo",
    "admin.users.title": "Gestión de Usuarios",
    "admin.users.loading": "Cargando usuarios...",
    "admin.users.nousers": "No se encontraron usuarios.",
    "admin.users.email": "Email",
    "admin.users.role": "Rol",
    "admin.users.roles": "Roles y Gestión",
    "admin.users.created": "Creado",
    "admin.users.lastsignin": "Último Inicio de Sesión",
    "admin.users.never": "Nunca",
    "admin.users.actions": "Acciones",
    "admin.users.admin": "Administrador",
    "admin.users.viewer": "Visor",
    "admin.users.primary": "Principal",
    "admin.users.user": "Usuario",
    "admin.users.norole": "Sin rol asignado",
    "admin.users.deleteconfirm": "¿Está seguro de que desea eliminar al usuario",
    "admin.users.cannotdelete": "No se puede eliminar al administrador principal",
    "admin.users.deleteuser": "Eliminar Usuario",
    "admin.users.yourself": "Tú (no puedes eliminarte a ti mismo)",
    "admin.users.makeadmin": "Hacer Administrador",
    "admin.users.makeviewer": "Hacer Visor",
    "admin.users.removeadmin": "Quitar Administrador",
    "admin.users.removeviewer": "Quitar Visor",
    "admin.users.roleassigned": "Rol asignado exitosamente",
    "admin.users.roleremoved": "Rol eliminado exitosamente",
    "admin.users.roleerror": "Error al actualizar el rol",
    "admin.users.createuser": "Crear Usuario",
    "admin.users.createnew": "Crear Nuevo Usuario",
    "admin.users.createdescription": "Agregue un nuevo usuario al sistema y opcionalmente asígnele un rol",
    "admin.users.password": "Contraseña",
    "admin.users.passwordhint": "Mínimo 6 caracteres",
    "admin.users.assignrole": "Asignar Rol (Opcional)",
    "admin.users.cancel": "Cancelar",
    "admin.users.create": "Crear",
    "admin.users.createsuccess": "Usuario creado exitosamente",
    "admin.users.createerror": "Error al crear usuario",
    
    // Admin Contributor Requests
    "admin.contributors.title": "Solicitudes de Colaboradores",
    "admin.contributors.loading": "Cargando solicitudes...",
    "admin.contributors.norequests": "No se encontraron solicitudes de colaboradores",
    "admin.contributors.name": "Nombre",
    "admin.contributors.email": "Correo",
    "admin.contributors.role": "Rol Solicitado",
    "admin.contributors.message": "Mensaje",
    "admin.contributors.status": "Estado",
    "admin.contributors.created": "Enviado",
    "admin.contributors.actions": "Acciones",
    "admin.contributors.field.name": "Nombre",
    "admin.contributors.field.email": "Correo",
    "admin.contributors.field.role": "Rol Solicitado",
    "admin.contributors.field.message": "Mensaje",
    "admin.contributors.field.status": "Estado",
    "admin.contributors.field.date": "Fecha",
    "admin.contributors.status.all": "Todos",
    "admin.contributors.status.pending": "Pendiente",
    "admin.contributors.status.approved": "Aprobado",
    "admin.contributors.status.rejected": "Rechazado",
    "admin.contributors.roles.blogeditor": "Editor de Blog",
    "admin.contributors.roles.bibleschooleditor": "Editor de Escuela Bíblica",
    "admin.contributors.roles.both": "Ambos (Blog + Escuela Bíblica)",
    "admin.contributors.pending": "Pendiente",
    "admin.contributors.approved": "Aprobado e invitado exitosamente",
    "admin.contributors.rejected": "Rechazado exitosamente",
    "admin.contributors.approve": "Aprobar y Crear Usuario",
    "admin.contributors.reject": "Rechazar",
    "admin.contributors.delete": "Eliminar",
    "admin.contributors.approvesuccess": "Solicitud aprobada y usuario invitado",
    "admin.contributors.approveerror": "Error al aprobar la solicitud",
    "admin.contributors.rejectsuccess": "Solicitud rechazada",
    "admin.contributors.rejecterror": "Error al rechazar la solicitud",
    "admin.contributors.deletesuccess": "Solicitud eliminada",
    "admin.contributors.deleteerror": "Error al eliminar la solicitud",
    "admin.contributors.deleteconfirm": "¿Está seguro de que desea eliminar esta solicitud?",
    "admin.contributors.rejectconfirm": "¿Está seguro de que desea rechazar esta solicitud?",
    "admin.contributors.nomessage": "No se proporcionó mensaje",
    
    "admin.sermons.title": "Gestionar Sermones",
    "admin.sermons.addsermon": "Agregar Sermón",
    "admin.sermons.editsermon": "Editar Sermón",
    "admin.sermons.addnew": "Agregar Nuevo Sermón",
    "admin.sermons.filldetails": "Completa los detalles del sermón a continuación",
    "admin.sermons.title.label": "Título",
    "admin.sermons.speaker.label": "Predicador",
    "admin.sermons.date.label": "Fecha",
    "admin.sermons.scripture.label": "Referencia de Escritura",
    "admin.sermons.scripture.placeholder": "ej., Juan 3:16",
    "admin.sermons.description.label": "Descripción",
    "admin.sermons.audiourl.label": "URL de Audio",
    "admin.sermons.audiourl.placeholder": "https://example.com/audio.mp3",
    "admin.sermons.videourl.label": "URL de Video",
    "admin.sermons.videourl.placeholder": "https://youtube.com/watch?v=...",
    "admin.sermons.cancel": "Cancelar",
    "admin.sermons.create": "Crear",
    "admin.sermons.update": "Actualizar",
    "admin.sermons.loading": "Cargando sermones...",
    "admin.sermons.nosermons": "Aún no hay sermones. Haz clic en \"Agregar Sermón\" para crear uno.",
    "admin.sermons.deleteconfirm": "¿Estás seguro de que quieres eliminar este sermón?",
    "admin.sermons.media": "Media",
    "admin.sermons.mediatype.audio": "Audio",
    "admin.sermons.mediatype.video": "Video",
    "admin.sermons.translations": "Gestionar Traducciones",
    "admin.sermons.translations.title": "Traducciones del Sermón",
    "admin.sermons.translations.description": "Agrega traducciones para este sermón en diferentes idiomas",
    "admin.sermons.translations.language": "Idioma",
    "admin.sermons.translations.english": "Inglés",
    "admin.sermons.translations.russian": "Ruso",
    "admin.sermons.translations.spanish": "Español",
    "admin.sermons.translations.save": "Guardar Traducciones",
    "admin.sermons.translations.success": "Traducciones guardadas exitosamente",
    "admin.sermons.translations.error": "Error al guardar traducciones",
    "admin.sermons.updatedsuccess": "Sermón actualizado exitosamente.",
    "admin.sermons.addedsuccess": "Sermón agregado exitosamente.",
    "admin.sermons.deletedsuccess": "Sermón eliminado exitosamente.",
    "admin.sermons.saveerror": "Error al guardar el sermón.",
    "admin.sermons.fetcherror": "Error al cargar los sermones.",
    "admin.sermons.deleteerror": "Error al eliminar el sermón.",
    "admin.sermons.published": "Publicado",
    
    // Admin - Announcements
    "admin.announcements.title": "Gestionar Anuncios",
    "admin.announcements.add": "Agregar Anuncio",
    "admin.announcements.edit": "Editar Anuncio",
    "admin.announcements.editdesc": "Actualiza los detalles del anuncio a continuación.",
    "admin.announcements.adddesc": "Completa los detalles para el nuevo anuncio.",
    "admin.announcements.titlefield": "Título",
    "admin.announcements.titleplaceholder": "Título del anuncio",
    "admin.announcements.content": "Contenido",
    "admin.announcements.contentplaceholder": "Contenido del anuncio",
    "admin.announcements.published": "Publicar anuncio",
    "admin.announcements.create": "Crear",
    "admin.announcements.update": "Actualizar",
    "admin.announcements.cancel": "Cancelar",
    "admin.announcements.loading": "Cargando anuncios...",
    "admin.announcements.noannouncements": "Aún no hay anuncios. Crea tu primer anuncio.",
    "admin.announcements.deleteconfirm": "¿Estás seguro de que quieres eliminar este anuncio?",
    "admin.announcements.translations": "Gestionar Traducciones",
    "admin.announcements.translationsdesc": "Traducir \"{title}\" a otros idiomas",
    "admin.announcements.translations.russian": "Ruso (RU)",
    "admin.announcements.translations.spanish": "Español (ES)",
    "admin.announcements.translations.title": "Título",
    "admin.announcements.translations.content": "Contenido",
    "admin.announcements.translations.save": "Guardar Traducciones",
    "admin.announcements.translations.cancel": "Cancelar",
    "admin.announcements.status.published": "Publicado",
    "admin.announcements.status.draft": "Borrador",
    "admin.announcements.updatedsuccess": "Anuncio actualizado exitosamente",
    "admin.announcements.addedsuccess": "Anuncio creado exitosamente",
    "admin.announcements.deletedsuccess": "Anuncio eliminado exitosamente",
    "admin.announcements.saveerror": "Error al guardar el anuncio",
    "admin.announcements.fetcherror": "Error al cargar los anuncios",
    "admin.announcements.deleteerror": "Error al eliminar el anuncio",
    "admin.announcements.translationssuccess": "Traducciones guardadas exitosamente",
    "admin.announcements.translationserror": "Error al guardar las traducciones",
    
    "admin.users.deletedsuccess": "Usuario eliminado exitosamente.",
    "admin.users.deleteerror": "Error al eliminar el usuario.",
    "admin.users.fetcherror": "Error al cargar los usuarios.",
    "admin.donations.deletedsuccess": "Donación eliminada exitosamente.",
    "admin.donations.deleteerror": "Error al eliminar la donación.",
    "admin.donations.fetcherror": "Error al cargar las donaciones.",
    "admin.photos.title": "Fotos de Misiones",
    "admin.photos.upload": "Subir Foto",
    "admin.photos.uploadnew": "Subir Nueva Foto",
    "admin.photos.edit": "Editar Foto",
    "admin.photos.description": "Agregar fotos a las galerías de misiones",
    "admin.photos.file": "Archivo de Foto",
    "admin.photos.phototitle": "Título de la Foto",
    "admin.photos.photodescription": "Descripción de la Foto",
    "admin.photos.displayorder": "Orden de Visualización",
    "admin.photos.order": "Orden",
    "admin.photos.cancel": "Cancelar",
    "admin.photos.uploadbutton": "Subir",
    "admin.photos.update": "Actualizar",
    "admin.photos.editbutton": "Editar",
    "admin.photos.deletebutton": "Eliminar",
    "admin.photos.loading": "Cargando fotos...",
    "admin.photos.nophotos": "No se encontraron fotos.",
    "admin.photos.fetcherror": "Error al cargar las fotos.",
    "admin.photos.uploadsuccess": "Foto subida exitosamente.",
    "admin.photos.uploaderror": "Error al subir la foto.",
    "admin.photos.updatesuccess": "Foto actualizada exitosamente.",
    "admin.photos.updateerror": "Error al actualizar la foto.",
    "admin.photos.deletesuccess": "Foto eliminada exitosamente.",
    "admin.photos.deleteerror": "Error al eliminar la foto.",
    "admin.photos.deleteconfirm": "¿Está seguro de que desea eliminar esta foto?",
    "admin.photos.nofile": "Por favor seleccione un archivo para subir.",
    
    // Admin Bible School
    "admin.bibleschool.title": "Gestión de Escuela Bíblica",
    "admin.bibleschool.add": "Agregar Curso",
    "admin.bibleschool.create": "Crear Curso",
    "admin.bibleschool.edit": "Editar Curso",
    "admin.bibleschool.update": "Actualizar Curso",
    "admin.bibleschool.cancel": "Cancelar",
    "admin.bibleschool.loading": "Cargando cursos...",
    "admin.bibleschool.nocourses": "No se encontraron cursos.",
    "admin.bibleschool.filter.all": "Todos",
    "admin.bibleschool.filter.published": "Publicado",
    "admin.bibleschool.filter.draft": "Borrador",
    "admin.bibleschool.field.title": "Título",
    "admin.bibleschool.field.description": "Descripción",
    "admin.bibleschool.field.instructor": "Instructor",
    "admin.bibleschool.field.duration": "Duración",
    "admin.bibleschool.field.duration.placeholder": "ej., 8 semanas, 3 meses",
    "admin.bibleschool.field.level": "Nivel",
    "admin.bibleschool.field.level.placeholder": "ej., Principiante, Intermedio, Avanzado",
    "admin.bibleschool.field.schedule": "Horario",
    "admin.bibleschool.field.schedule.placeholder": "ej., Lunes 7:00 PM",
    "admin.bibleschool.field.maxstudents": "Máx. Estudiantes",
    "admin.bibleschool.field.image": "Imagen",
    "admin.bibleschool.field.published": "Publicado",
    "admin.bibleschool.status": "Estado",
    "admin.bibleschool.actions": "Acciones",
    "admin.bibleschool.translations": "Traducciones",
    "admin.bibleschool.status.published": "Publicado",
    "admin.bibleschool.status.draft": "Borrador",
    
    // Admin Blog
    "admin.blog.title": "Gestión de Blog",
    "admin.blog.add": "Agregar Artículo",
    "admin.blog.create": "Crear Artículo",
    "admin.blog.edit": "Editar Artículo",
    "admin.blog.update": "Actualizar Artículo",
    "admin.blog.cancel": "Cancelar",
    "admin.blog.loading": "Cargando artículos...",
    "admin.blog.noposts": "No se encontraron artículos.",
    "admin.blog.filter.all": "Todos",
    "admin.blog.filter.published": "Publicado",
    "admin.blog.filter.draft": "Borrador",
    "admin.blog.field.title": "Título",
    "admin.blog.field.slug": "URL",
    "admin.blog.field.slug.placeholder": "generado-del-titulo",
    "admin.blog.field.author": "Autor",
    "admin.blog.field.category": "Categoría",
    "admin.blog.field.excerpt": "Extracto",
    "admin.blog.field.content": "Contenido",
    "admin.blog.field.image": "URL de Imagen",
    "admin.blog.field.published": "Publicado",
    "admin.blog.status": "Estado",
    "admin.blog.actions": "Acciones",
    "admin.blog.translations": "Traducciones",
    "admin.blog.translations.title": "Traducciones del Artículo",
    "admin.blog.translations.desc": "Agregue traducciones para este artículo en diferentes idiomas",
    "admin.blog.translations.save": "Guardar Traducciones",
    "admin.blog.status.published": "Publicado",
    "admin.blog.status.draft": "Borrador",
    
    // Membership
    "membership.title": "Membresía de la Iglesia",
    "membership.subtitle": "Únase a nuestra familia de la iglesia y sea parte activa de nuestra comunidad",
    "membership.benefit1.title": "Conexión Comunitaria",
    "membership.benefit1.description": "Conéctese con otros miembros y construya relaciones duraderas",
    "membership.benefit2.title": "Participación Activa",
    "membership.benefit2.description": "Participe en actividades y ministerios de la iglesia",
    "membership.benefit3.title": "Dar Convenientemente",
    "membership.benefit3.description": "Fácil seguimiento de diezmos y cuotas en línea",
    "membership.notloggedin": "Inicie sesión o cree una cuenta para comenzar su solicitud de membresía",
    "membership.signinsignup": "Iniciar Sesión / Registrarse",
    "membership.gotoportal": "Ir al Portal de Miembros",
    "membership.readytojoin": "¿Listo para unirse a nuestra familia de la iglesia?",
    "membership.becomemember": "Hacerse Miembro",
    "membership.whattoexpect": "Qué Esperar",
    "membership.step1": "Complete un formulario de registro simple",
    "membership.step2": "Proporcione información de contacto básica",
    "membership.step3": "Su solicitud será revisada por la administración de la iglesia",
    "membership.step4": "Una vez aprobado, tendrá acceso al portal de miembros",
    "membership.step5": "Realice pagos de diezmos y cuotas en línea",
    "membership.step6": "Conéctese con otros miembros de la iglesia",
    
    // Member Portal
    "member.portal.title": "Portal de Miembros",
    "member.portal.welcome": "¡Bienvenido!",
    "member.portal.needregistration": "Necesita completar su registro de miembro para acceder al portal.",
    "member.portal.completeregistration": "Completar Registro",
    "member.portal.tabs.profile": "Perfil",
    "member.portal.tabs.payments": "Pagos",
    "member.portal.tabs.family": "Familia",
    "member.portal.tabs.security": "Seguridad",
    
    // Member Profile
    "member.profile.title": "Perfil de Miembro",
    "member.profile.description": "Ver y actualizar su información",
    "member.profile.edit": "Editar Perfil",
    "member.profile.fullName": "Nombre Completo",
    "member.profile.email": "Correo Electrónico",
    "member.profile.phone": "Teléfono",
    "member.profile.address": "Dirección",
    "member.profile.numberOfKids": "Número de Hijos",
    "member.profile.notProvided": "No proporcionado",
    "member.profile.memberSince": "Miembro Desde",
    "member.profile.saveChanges": "Guardar Cambios",
    "member.profile.saving": "Guardando...",
    "member.profile.cancel": "Cancelar",
    "member.profile.success": "Éxito",
    "member.profile.updateSuccess": "Perfil actualizado exitosamente",
    "member.profile.error": "Error",
    "member.profile.updateError": "Error al actualizar el perfil",
    
    // Member Payments
    "member.payments.makePayment": "Realizar un Pago",
    "member.payments.submitPayment": "Enviar diezmo o cuotas mensuales",
    "member.payments.tithe": "Diezmo",
    "member.payments.titheDescription": "Enviar su ofrenda de diezmo",
    "member.payments.payTithe": "Pagar Diezmo",
    "member.payments.monthlyDues": "Cuotas Mensuales",
    "member.payments.monthlyRentalDues": "Cuotas Mensuales de Alquiler",
    "member.payments.duesDescription": "Pagar sus cuotas de membresía mensual",
    "member.payments.payDues": "Pagar Cuotas",
    "member.payments.payMonthlyRentalDues": "Pagar Cuotas de Alquiler Mensuales",
    "member.payments.duesSchedule": "Calendario de Cuotas Mensuales",
    "member.payments.monthlyPaymentSchedule": "Calendario de Pagos Mensuales",
    "member.payments.scheduleAmount": "$30 por cada miembro de la iglesia",
    "member.payments.history": "Historial de Pagos",
    "member.payments.historyDescription": "Ver todos sus pagos anteriores",
    "member.payments.loading": "Cargando...",
    "member.payments.noHistory": "Aún no hay historial de pagos",
    "member.payments.date": "Fecha",
    "member.payments.type": "Tipo",
    "member.payments.amount": "Monto",
    "member.payments.status": "Estado",
    "member.payments.error": "Error",
    "member.payments.loadError": "Error al cargar el historial de pagos",
    
    // MFA
    "mfa.title": "Autenticación de Dos Factores",
    "mfa.enable": "Habilitar Autenticación de Dos Factores",
    "mfa.disable": "Desactivar Autenticación de Dos Factores",
    "mfa.setupDescription": "Añade una capa adicional de seguridad a tu cuenta",
    "mfa.setupExplanation": "La autenticación de dos factores (2FA) añade una capa adicional de seguridad al requerir un código de verificación de tu teléfono cuando inicias sesión.",
    "mfa.authenticatorRequired": "Necesitarás una aplicación de autenticación como Google Authenticator, Authy o Microsoft Authenticator.",
    "mfa.setupButton": "Configurar Autenticación de Dos Factores",
    "mfa.protectedDescription": "Tu cuenta está protegida con 2FA",
    "mfa.enabledMessage": "La autenticación de dos factores está habilitada. Necesitarás tu aplicación de autenticación al iniciar sesión.",
    
    // Family Management
    "member.family.title": "Relaciones Familiares",
    "member.family.description": "Administrar sus conexiones familiares",
    "member.family.addRelationship": "Agregar Relación",
    "member.family.addTitle": "Agregar Relación Familiar",
    "member.family.addDescription": "Conectar con otro miembro de la iglesia",
    "member.family.churchMember": "Miembro de la Iglesia",
    "member.family.selectMember": "Seleccionar un miembro",
    "member.family.relationshipType": "Tipo de Relación",
    "member.family.selectRelationship": "Seleccionar relación",
    "member.family.spouse": "Cónyuge",
    "member.family.parent": "Padre/Madre",
    "member.family.child": "Hijo/Hija",
    "member.family.sibling": "Hermano/Hermana",
    "member.family.addButton": "Agregar Relación",
    "member.family.loading": "Cargando...",
    "member.family.noRelationships": "Aún no se han agregado relaciones familiares",
    "member.family.name": "Nombre",
    "member.family.relationship": "Relación",
    "member.family.actions": "Acciones",
    "member.family.success": "Éxito",
    "member.family.addSuccess": "Relación familiar agregada",
    "member.family.removeSuccess": "Relación eliminada",
    "member.family.error": "Error",
    "member.family.loadError": "Error al cargar las relaciones familiares",
    "member.family.selectError": "Por favor seleccione un miembro y tipo de relación",
    "member.family.addError": "Error al agregar la relación",
    "member.family.removeError": "Error al eliminar la relación",
    
    // Admin Member Management
    "admin.members.title": "Gestión de Miembros",
    "admin.members.directory.title": "Directorio de Miembros",
    "admin.members.directory.description": "Ver y gestionar todos los miembros de la iglesia",
    "admin.members.directory.loading": "Cargando...",
    "admin.members.directory.name": "Nombre",
    "admin.members.directory.email": "Correo",
    "admin.members.directory.phone": "Teléfono",
    "admin.members.directory.status": "Estado",
    "admin.members.directory.children": "Niños",
    "admin.members.directory.actions": "Acciones",
    "admin.members.directory.na": "N/A",
    "admin.members.directory.approve": "Aprobar",
    "admin.members.directory.view": "Ver",
    "admin.members.directory.delete": "Eliminar",
    "admin.members.details.title": "Detalles del Miembro",
    "admin.members.details.address": "Dirección",
    "admin.members.details.paymenthistory": "Historial de Pagos",
    "admin.members.details.nopayments": "Aún no hay pagos",
    "admin.members.details.date": "Fecha",
    "admin.members.details.type": "Tipo",
    "admin.members.details.amount": "Monto",
    "admin.members.status.active": "activo",
    "admin.members.status.pending": "pendiente",
    "admin.members.status.inactive": "inactivo",
    "admin.members.approve.success": "Miembro aprobado",
    "admin.members.approve.error": "Error al aprobar miembro",
    "admin.members.delete.confirm": "Eliminar Miembro",
    "admin.members.delete.description": "¿Está seguro de que desea eliminar la membresía de {name}? Esta acción no se puede deshacer.",
    "admin.members.delete.cancel": "Cancelar",
    "admin.members.delete.button": "Eliminar",
    "admin.members.delete.success": "La membresía de {name} ha sido eliminada",
    "admin.members.delete.error": "Error al eliminar miembro",
    
    // Member Registration
    "member.registration.title": "Registro de Miembros",
    "member.registration.description": "Complete su información para convertirse en miembro de la iglesia",
    "member.registration.fullname": "Nombre Completo",
    "member.registration.email": "Correo Electrónico",
    "member.registration.phone": "Teléfono",
    "member.registration.address": "Dirección",
    "member.registration.children": "Número de Niños",
    "member.registration.submit": "Enviar Solicitud",
    "member.registration.submitting": "Enviando...",
    "member.registration.success": "Éxito",
    "member.registration.successmessage": "Su solicitud de membresía ha sido enviada para aprobación",
    "member.registration.successmessage.admin": "Su membresía ha sido activada automáticamente",
    "member.registration.error": "Error",
    "member.registration.errormessage": "Error al enviar el registro",
    "member.registration.alreadymember": "Ya es Miembro",
    "member.registration.alreadymessage": "Ya tiene una solicitud de membresía con estado: {status}",
    "member.registration.checking": "Verificando estado de membresía...",
    
    // Music - Member View
    "nav.music": "Música",
    "music.title": "Música de la Iglesia",
    "music.accessDenied": "Acceso denegado",
    "music.accessDeniedDesc": "Debe ser miembro de la iglesia para acceder a los recursos musicales.",
    "music.noMusicAvailable": "No hay música disponible todavía",
    "music.play": "Reproducir",
    "music.download": "Descargar",
    "music.lyrics": "Letra",
    "music.chordSheet": "Acordes",
    "music.lyricsTitle": "{title} - Letra",
    "music.error": "Error",
    "music.allSongs": "Todas las canciones",
    "music.song": "canción",
    "music.songs2-4": "canciones",
    "music.songs5+": "canciones",
    "music.unknownArtist": "Artista desconocido",
    
    // Music Admin
    "admin.music.title": "Gestión de música",
    "admin.music.artists": "Artistas",
    "admin.music.addArtist": "Agregar artista",
    "admin.music.editArtist": "Editar artista",
    "admin.music.deleteArtist": "Eliminar artista",
    "admin.music.noArtists": "No hay artistas todavía",
    "admin.music.artistName": "Nombre del artista",
    "admin.music.artistNamePlaceholder": "Ingrese el nombre del artista",
    "admin.music.artistDescription": "Descripción del artista",
    "admin.music.artistDescriptionPlaceholder": "Ingrese la descripción del artista",
    "admin.music.artistImage": "URL de imagen del artista",
    "admin.music.selectArtist": "Seleccionar un artista",
    "admin.music.manageAlbums": "Gestionar álbumes",
    "admin.music.hideAlbums": "Ocultar álbumes",
    "admin.music.albumsFor": "Álbumes para",
    "admin.music.imageUrl": "URL de imagen",
    "admin.music.optional": "opcional",
    "admin.music.albums": "Álbumes",
    "admin.music.addDisk": "Agregar disco",
    "admin.music.editDisk": "Editar disco",
    "admin.music.disks": "Discos",
    "admin.music.noDisks": "No hay discos todavía",
    "admin.music.disk": "Disco",
    "admin.music.disksFor": "Discos para",
    "admin.music.manageDisks": "Gestionar discos",
    "admin.music.hideDisks": "Ocultar discos",
    "admin.music.manageSongs": "Gestionar canciones",
    "admin.music.hideSongs": "Ocultar canciones",
    "admin.music.songsForDisk": "Canciones del disco",
    "admin.music.songs": "Canciones",
    "admin.music.addAlbum": "Agregar álbum",
    "admin.music.editAlbum": "Editar álbum",
    "admin.music.deleteAlbum": "Eliminar álbum",
    "admin.music.albumTitle": "Título del álbum",
    "admin.music.albumDescription": "Descripción",
    "admin.music.albumCover": "URL de la portada del álbum",
    "admin.music.published": "Publicado",
    "admin.music.unpublished": "No publicado",
    "admin.music.save": "Guardar",
    "admin.music.cancel": "Cancelar",
    "admin.music.saving": "Guardando...",
    "admin.music.deleting": "Eliminando...",
    "admin.music.delete": "Eliminar",
    "admin.music.noAlbums": "No hay álbumes todavía",
    "admin.music.addSong": "Agregar canción",
    "admin.music.editSong": "Editar canción",
    "admin.music.deleteSong": "Eliminar canción",
    "admin.music.songTitle": "Título de la canción",
    "admin.music.artist": "Artista",
    "admin.music.duration": "Duración (ej., 3:45)",
    "admin.music.album": "Álbum",
    "admin.music.selectAlbum": "Seleccionar un álbum",
    "admin.music.noAlbum": "Sin álbum",
    "admin.music.audioFile": "Archivo de audio",
    "admin.music.uploadAudio": "Subir audio",
    "admin.music.uploading": "Subiendo...",
    "admin.music.lyrics": "Letra",
    "admin.music.chordSheet": "Partitura (PDF)",
    "admin.music.uploadChordSheet": "Subir partitura",
    "admin.music.displayOrder": "Orden de visualización",
    "admin.music.diskNumber": "Número de disco",
    "admin.music.diskNumberPlaceholder": "ej., 1, 2",
    "admin.music.diskName": "Nombre del disco",
    "admin.music.diskNamePlaceholder": "Nombre del disco (opcional)",
    "admin.music.songNumber": "Número de canción",
    "admin.music.songNumberPlaceholder": "ej., 1, 2, 3",
    "admin.music.uploadProgress": "Progreso de carga",
    "admin.music.selectAlbumFirst": "Seleccione un álbum para ver y administrar canciones",
    "admin.music.selectAlbumPlaceholder": "Elija un álbum...",
    "admin.music.description": "Descripción",
    "admin.music.songDescription": "Descripción de la canción",
    "admin.music.noSongs": "No hay canciones todavía",
    "admin.music.appleMusicLink": "Enlace de Apple Music",
    "admin.music.songDetails": "{title} de {artist}",
    "admin.music.duration.value": "Duración: {duration}",
    "admin.music.order.value": "Orden: {order}",
    "admin.music.success": "Éxito",
    "admin.music.albumSaved": "Álbum guardado y traducido automáticamente exitosamente",
    "admin.music.albumDeleted": "Álbum eliminado exitosamente",
    "admin.music.songSaved": "Canción guardada y traducida automáticamente exitosamente",
    "admin.music.songDeleted": "Canción eliminada exitosamente",
    "admin.music.errorSaving": "Error al guardar",
    "admin.music.errorDeleting": "Error al eliminar",
    "admin.music.errorUploading": "Error al subir el archivo",
    "admin.music.selectAlbumRequired": "Por favor seleccione un álbum primero. Cree un álbum antes de agregar canciones.",
    "admin.music.translating": "Traduciendo...",
    "admin.music.warning": "Advertencia",
  },
};
